import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Badge } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import PageHeader from '../components/common/PageHeader';
import {
  AlertCircle, CheckCircle, TrendingUp, TrendingDown, Lock, AlertTriangle, ChevronDown, ChevronUp,
  RefreshCw, Search, X, Activity, BarChart3, Download, Clock, Shield, Eye, Zap, GitBranch,
  ShieldCheck, Target, Gauge, Users, ArrowUpRight, ArrowDownRight, Layers, Plus, Trash2, Copy,
} from 'lucide-react';
import * as api from '../services/api';
import TrustTimelineTab from '../components/TrustTimelineTab';
import LiveExecutionCenter from '../components/LiveExecutionCenter';

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM — Credo.ai-inspired Enterprise Governance Aesthetic
   Self-contained tokens matching ComplianceCenterPage & PolicyConfigPage
   ══════════════════════════════════════════════════════════════════════════════ */

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)',
  mid: '#d4453a', soft: '#f8d7d4', muted: '#eab3ad', tint: '#fff5f4',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
};

const S = {
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

const ACCENT = {
  emerald:  { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:    { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:      { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:     { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:   { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:    { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

/* ─── Shared Base Styles ───────────────────────────────────────────────── */
const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};
const inputBase = {
  width: '100%', padding: '9px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: S[900],
  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
};
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 };
const selectStyle = { ...inputBase, cursor: 'pointer' };
const thStyle = {
  padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700,
  color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em',
  borderBottom: `2px solid ${S[200]}`, background: S[50],
};
const tdStyle = { padding: '14px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` };

/* ─── CSS Keyframes ─────────────────────────────────────────────────────── */
const TCStyles = () => (
  <style>{`
    @keyframes tcSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes tcSlideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes tcPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
    .tc-row:hover { background: ${S[50]} !important; }
    .tc-card { animation: tcSlideUp 0.3s ease forwards; }
    .tc-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
  `}</style>
);

/* ─── TC Card (accent top line) ────────────────────────────────────────── */
const TCCard = ({ children, accent, style = {} }) => (
  <div className="tc-card" style={{
    background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
    borderTop: accent ? `3px solid ${accent}` : `1px solid ${S[200]}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    transition: 'box-shadow 0.15s ease', ...style,
  }}>
    {children}
  </div>
);

/* ─── Stat Card with Icon Badge ────────────────────────────────────────── */
const TCStatCard = ({ label, value, icon: Icon, accent, subtitle, valueColor }) => (
  <TCCard accent={accent?.icon || BRAND.primary} style={{ flex: 1, minWidth: 180, padding: '20px 22px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: accent?.bg || BRAND.light,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={18} color={accent?.icon || BRAND.primary} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: S[500], marginBottom: 6,
          letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: valueColor || S[900], lineHeight: 1 }}>{value}</div>
        {subtitle && <div style={{ fontSize: 11, color: S[400], marginTop: 4 }}>{subtitle}</div>}
      </div>
    </div>
  </TCCard>
);

/* ─── Primary / Ghost Buttons ──────────────────────────────────────────── */
const PrimaryButton = ({ children, onClick, icon: Icon, disabled, style: s = {} }) => (
  <button onClick={onClick} disabled={disabled} style={{
    ...btnBase, padding: '9px 18px', fontSize: 12, fontWeight: 700,
    background: disabled ? S[300] : BRAND.gradient, color: '#fff',
    borderRadius: 8, boxShadow: disabled ? 'none' : '0 2px 8px rgba(226,74,58,0.18)',
    opacity: disabled ? 0.6 : 1, ...s,
  }}>
    {Icon && <Icon size={14} />} {children}
  </button>
);

const GhostButton = ({ children, onClick, icon: Icon }) => (
  <button onClick={onClick} style={{
    ...btnBase, padding: '9px 16px', fontSize: 12, fontWeight: 600,
    background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
  }}>
    {Icon && <Icon size={14} />} {children}
  </button>
);

const RefreshButton = ({ onClick, loading: spin }) => (
  <button onClick={onClick} style={{
    ...btnBase, padding: '9px 14px', background: S[100], color: S[600],
    borderRadius: 8, fontSize: 12, fontWeight: 600,
  }}>
    <RefreshCw size={14} style={spin ? { animation: 'tcSpin 1s linear infinite' } : {}} />
    Recalculate
  </button>
);

/* ─── Domain Color Maps ────────────────────────────────────────────────── */
const RISK_COLORS = { LOW: '#16a34a', MEDIUM: '#d97706', HIGH: '#ea580c', CRITICAL: '#dc2626' };
const RISK_BG = { LOW: ACCENT.emerald.bg, MEDIUM: ACCENT.amber.bg, HIGH: '#fff7ed', CRITICAL: ACCENT.rose.bg };

const AUTONOMY_COLORS = {
  SELF_AUTONOMOUS: BRAND.primary,
  SUPERVISED: '#ea6b5e',
  RESTRICTED: S[500],
  PROBATION: '#dc2626',
};
const AUTONOMY_BG = {
  SELF_AUTONOMOUS: BRAND.light,
  SUPERVISED: '#e3f4f1',
  RESTRICTED: S[100],
  PROBATION: ACCENT.rose.bg,
};

const ANOMALY_COLORS = {
  SCORE_DRIFT: '#ea580c', BLOCK_SPIKE: '#dc2626', UNUSUAL_PATTERN: '#0a766b', NONE: S[400],
};

/* ─── Utility Functions ────────────────────────────────────────────────── */
const formatScore = (score) => {
  if (score === undefined || score === null) return 'N/A';
  return Math.round(score);
};
const getScoreColor = (score) => {
  if (score === undefined || score === null) return S[400];
  if (score >= 90) return '#16a34a';
  if (score >= 75) return '#d97706';
  return '#dc2626';
};
const getScoreAccent = (score) => {
  if (score >= 90) return ACCENT.emerald;
  if (score >= 75) return ACCENT.amber;
  return ACCENT.rose;
};
const getTrustLabel = (score) => {
  if (score === undefined || score === null) return 'Unknown';
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Good';
  if (score >= 50) return 'Fair';
  return 'Poor';
};

/* ─── Sparkline ────────────────────────────────────────────────────────── */
const Sparkline = ({ data, width = 120, height = 30, color = '#0d9488' }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  });
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`sp-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        fill={`url(#sp-${color.replace('#','')})`}
        points={`0,${height} ${points.join(' ')} ${width},${height}`}
      />
      <polyline fill="none" stroke={color} strokeWidth={1.5} points={points.join(' ')} />
    </svg>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   TAB 1: TRUST DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */
const TrustDashboard = ({ dashboard, loading, onRefresh }) => {
  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: S[400] }}>Loading dashboard...</div>;
  if (!dashboard) return <div style={{ padding: 48, textAlign: 'center', color: S[400] }}>No data available.</div>;

  const fleetScore = dashboard.averageScore ?? dashboard.fleetAverageScore ?? 0;
  const agentsOnProbation = dashboard.probationCount ?? dashboard.agentsOnProbation ?? 0;
  const activeAnomalies = dashboard.anomalyCount ?? dashboard.activeAnomalies ?? 0;
  const totalAgents = dashboard.totalAgents || 0;
  const riskDist = dashboard.agentsByRisk || dashboard.riskDistribution || {};
  const totalRisk = Object.values(riskDist).reduce((a, b) => a + b, 0) || 1;
  const autonomyDist = dashboard.agentsByAutonomy || dashboard.autonomyDistribution || {};
  const transitions = dashboard.recentTransitions || [];
  const rawScoreDist = dashboard.scoreDistribution || {};
  const scoreDistribution = {
    '<70': rawScoreDist['below_70'] || 0,
    '70-79': rawScoreDist['70_79'] || 0,
    '80-89': rawScoreDist['80_89'] || 0,
    '90-100': rawScoreDist['90_100'] || 0,
  };

  return (
    <div>
      {/* Hero Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
        <TCStatCard label="Total Agents" value={totalAgents} icon={Users} accent={ACCENT.slate} subtitle="Active fleet" />
        <TCStatCard label="Fleet Trust Score" value={formatScore(fleetScore)} icon={ShieldCheck}
          accent={getScoreAccent(fleetScore)} subtitle={getTrustLabel(fleetScore)} valueColor={getScoreColor(fleetScore)} />
        <TCStatCard label="On Probation" value={agentsOnProbation} icon={Lock}
          accent={agentsOnProbation > 0 ? ACCENT.rose : ACCENT.emerald}
          subtitle={agentsOnProbation > 0 ? 'Action needed' : 'None'} valueColor={agentsOnProbation > 0 ? '#dc2626' : '#16a34a'} />
        <TCStatCard label="Active Anomalies" value={activeAnomalies} icon={AlertTriangle}
          accent={activeAnomalies > 0 ? ACCENT.rose : ACCENT.emerald}
          subtitle={activeAnomalies > 0 ? 'Review needed' : 'Clear'} valueColor={activeAnomalies > 0 ? '#dc2626' : '#16a34a'} />
      </div>

      {/* Risk Distribution */}
      <TCCard accent={BRAND.primary} style={{ marginBottom: 20, padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: S[900], marginBottom: 18 }}>Risk Level Distribution</div>
        <div style={{ display: 'flex', gap: 12 }}>
          {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((level) => {
            const count = riskDist[level] || 0;
            const pct = totalRisk > 0 ? Math.round((count / totalRisk) * 100) : 0;
            return (
              <div key={level} style={{
                flex: 1, padding: '18px 16px', borderRadius: 10,
                background: RISK_BG[level], border: `1px solid ${RISK_COLORS[level]}22`,
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: RISK_COLORS[level], marginBottom: 4 }}>{count}</div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: S[800] }}>{level}</div>
                <div style={{ fontSize: 11, color: S[400], marginTop: 4 }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </TCCard>

      {/* Autonomy Distribution */}
      <TCCard style={{ marginBottom: 20, padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: S[900], marginBottom: 18 }}>Autonomy Level Distribution</div>
        <div style={{ display: 'flex', gap: 12 }}>
          {['SELF_AUTONOMOUS', 'SUPERVISED', 'RESTRICTED', 'PROBATION'].map((level) => {
            const count = autonomyDist[level] || 0;
            return (
              <div key={level} style={{
                flex: 1, padding: '18px 16px', borderRadius: 10,
                background: AUTONOMY_BG[level], border: `1px solid ${AUTONOMY_COLORS[level]}22`,
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: AUTONOMY_COLORS[level], marginBottom: 4 }}>{count}</div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: S[800] }}>
                  {level.replace(/_/g, ' ')}
                </div>
              </div>
            );
          })}
        </div>
      </TCCard>

      {/* Score Distribution */}
      <TCCard style={{ marginBottom: 20, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: S[900] }}>Score Distribution</div>
          <div style={{ fontSize: 11, color: S[400] }}>{totalAgents} agent{totalAgents !== 1 ? 's' : ''} monitored</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { bucket: '90-100', label: '90–100', color: '#16a34a', bg: ACCENT.emerald.bg, tag: 'Excellent' },
            { bucket: '80-89',  label: '80–89',  color: '#65a30d', bg: '#f7fee7', tag: 'Good' },
            { bucket: '70-79',  label: '70–79',  color: '#d97706', bg: ACCENT.amber.bg, tag: 'Fair' },
            { bucket: '<70',    label: '< 70',   color: '#dc2626', bg: ACCENT.rose.bg, tag: 'At Risk' },
          ].map(({ bucket, label, color, bg, tag }) => {
            const count = scoreDistribution[bucket] || 0;
            const pct = totalAgents > 0 ? (count / totalAgents * 100) : 0;
            return (
              <div key={bucket}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: color }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: S[900] }}>{label}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color, background: bg, padding: '2px 8px', borderRadius: 10 }}>{tag}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: count > 0 ? color : S[300] }}>{count}</span>
                    {pct > 0 && <span style={{ fontSize: 11, color: S[400] }}>({Math.round(pct)}%)</span>}
                  </div>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: S[100], overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 4,
                    width: pct > 0 ? `${Math.max(pct, 3)}%` : '0%',
                    background: `linear-gradient(90deg, ${color}, ${color}dd)`,
                    transition: 'width 0.6s ease',
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </TCCard>

      {/* Recent Transitions */}
      <TCCard style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px 0', fontSize: 15, fontWeight: 700, color: S[900] }}>Recent Autonomy Transitions</div>
        {transitions.length === 0 ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: S[400] }}>No recent transitions</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
            <thead><tr>
              <th style={thStyle}>Agent Name</th>
              <th style={thStyle}>Transition</th>
              <th style={thStyle}>Trigger</th>
              <th style={thStyle}>Timestamp</th>
            </tr></thead>
            <tbody>
              {transitions.slice(0, 10).map((t, i) => (
                <tr key={i} className="tc-row" style={{ background: '#fff' }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{t.agentName || 'Unknown'}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: S[500] }}>{t.oldLevel || 'N/A'}</span>
                      {t.upgradeType
                        ? <ArrowUpRight size={14} color="#16a34a" />
                        : <ArrowDownRight size={14} color="#dc2626" />}
                      <span style={{ fontSize: 12, color: S[500] }}>{t.newLevel || 'N/A'}</span>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block', padding: '3px 8px', borderRadius: 6,
                      fontSize: 11, fontWeight: 600, background: S[100], color: S[700],
                    }}>{t.triggerType || 'Manual'}</span>
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: S[500] }}>
                    {t.timestamp ? new Date(t.timestamp).toLocaleString() : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TCCard>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   TAB 2: AGENT FLEET
   ═══════════════════════════════════════════════════════════════════════════ */
const AgentFleetTab = ({ profiles, loading, onRecalculate, onProbation, onRelease, onOverride, canModify }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [autonomyFilter, setAutonomyFilter] = useState('');
  const [probationFilter, setProbationFilter] = useState(false);
  const [sortBy, setSortBy] = useState('score-desc');
  const [expandedAgent, setExpandedAgent] = useState(null);
  const pag = usePaginationState(10);

  const agentOptions = useMemo(() =>
    (profiles || []).map(p => ({ id: p.agentId, name: p.agentName || p.agentId })).sort((a, b) => a.name.localeCompare(b.name)),
  [profiles]);

  let filtered = (profiles || []).filter((p) => {
    if (selectedAgentId && p.agentId !== selectedAgentId) return false;
    if (searchTerm && !(p.agentName || '').toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (riskFilter && p.riskLevel !== riskFilter) return false;
    if (autonomyFilter && (p.currentAutonomyLevel || p.autonomyLevel || '') !== autonomyFilter) return false;
    if (probationFilter && !p.probationActive && !p.onProbation) return false;
    return true;
  });

  filtered.sort((a, b) => {
    const sa = parseFloat(a.currentScore || a.trustScore) || 0;
    const sb = parseFloat(b.currentScore || b.trustScore) || 0;
    if (sortBy === 'score-asc') return sa - sb;
    if (sortBy === 'score-desc') return sb - sa;
    if (sortBy === 'name') return (a.agentName || '').localeCompare(b.agentName || '');
    if (sortBy === 'risk') {
      const ro = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
      return (ro[a.riskLevel] || 999) - (ro[b.riskLevel] || 999);
    }
    return 0;
  });

  const paginated = pag.paginate(filtered);

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: S[400] }}>Loading fleet...</div>;

  return (
    <div>
      {/* Filters */}
      <TCCard style={{ marginBottom: 20, padding: '18px 22px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={labelStyle}>Search Agent Name</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: S[400] }} />
              <input type="text" placeholder="e.g., DataAnalyzer" value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setSelectedAgentId(''); pag.setPage(0); }}
                style={{ ...inputBase, paddingLeft: 32 }} />
              {searchTerm && (
                <button onClick={() => { setSearchTerm(''); pag.setPage(0); }}
                  style={{ position: 'absolute', right: 8, top: 8, background: 'none', border: 'none', cursor: 'pointer', color: S[400], padding: 0 }}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div style={{ minWidth: 200 }}>
            <label style={labelStyle}>Select Agent</label>
            <select value={selectedAgentId} onChange={(e) => { setSelectedAgentId(e.target.value); setSearchTerm(''); pag.setPage(0); }} style={selectStyle}>
              <option value="">All Agents ({agentOptions.length})</option>
              {agentOptions.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 140 }}>
            <label style={labelStyle}>Risk Level</label>
            <select value={riskFilter} onChange={(e) => { setRiskFilter(e.target.value); pag.setPage(0); }} style={selectStyle}>
              <option value="">All</option>
              {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(l => <option key={l} value={l}>{l.charAt(0) + l.slice(1).toLowerCase()}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 140 }}>
            <label style={labelStyle}>Autonomy</label>
            <select value={autonomyFilter} onChange={(e) => { setAutonomyFilter(e.target.value); pag.setPage(0); }} style={selectStyle}>
              <option value="">All</option>
              {['SELF_AUTONOMOUS', 'SUPERVISED', 'RESTRICTED', 'PROBATION'].map(l =>
                <option key={l} value={l}>{l.replace(/_/g, ' ').replace(/\b\w/g, c => c)}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 140 }}>
            <label style={labelStyle}>Sort By</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={selectStyle}>
              <option value="score-desc">Score (Worst First)</option>
              <option value="score-asc">Score (Best First)</option>
              <option value="name">Name (A-Z)</option>
              <option value="risk">Risk Level</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', paddingBottom: 2 }}>
            <input type="checkbox" checked={probationFilter} onChange={(e) => { setProbationFilter(e.target.checked); pag.setPage(0); }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: S[600] }}>Probation Only</span>
          </label>
        </div>
      </TCCard>

      {/* Fleet Table */}
      <TCCard style={{ overflow: 'hidden', marginBottom: 20, padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...thStyle, width: 36 }} />
            <th style={thStyle}>Agent Name</th>
            <th style={thStyle}>Trust Score</th>
            <th style={thStyle}>Autonomy Level</th>
            <th style={thStyle}>Risk Level</th>
            <th style={thStyle}>Evaluations</th>
            <th style={thStyle}>Blocks/Warns</th>
            <th style={thStyle}>Anomaly</th>
            <th style={thStyle}>Status</th>
          </tr></thead>
          <tbody>
            {paginated.map((profile) => {
              const score = parseFloat(profile.currentScore || profile.trustScore) || 0;
              const autonomy = profile.currentAutonomyLevel || profile.autonomyLevel || '';
              const isExpanded = expandedAgent === profile.agentId;
              const bc = (profile.totalLifetimeBlocks || 0) + (profile.totalLifetimeWarnings || 0) || profile.blockWarnCount || 0;

              return (
                <React.Fragment key={profile.agentId}>
                  <tr className="tc-row" style={{ background: isExpanded ? '#fce8e6' : '#fff', cursor: 'pointer', boxShadow: isExpanded ? `inset 4px 0 0 ${BRAND.primary}` : 'none' }}>
                    <td style={tdStyle} onClick={() => setExpandedAgent(isExpanded ? null : profile.agentId)}>
                      {isExpanded ? <ChevronUp size={14} color={S[500]} /> : <ChevronDown size={14} color={S[400]} />}
                    </td>
                    <td style={tdStyle} onClick={() => setExpandedAgent(isExpanded ? null : profile.agentId)}>
                      <span style={{ fontWeight: 700, color: S[900] }}>{profile.agentName || 'Unknown'}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px', borderRadius: 8,
                        background: getScoreAccent(score).bg,
                        fontSize: 13, fontWeight: 700, color: getScoreColor(score),
                      }}>
                        <Shield size={12} /> {formatScore(score)}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-block', padding: '4px 10px', borderRadius: 6,
                        fontSize: 11, fontWeight: 700,
                        background: AUTONOMY_BG[autonomy] || S[100],
                        color: AUTONOMY_COLORS[autonomy] || S[600],
                        border: `1px solid ${AUTONOMY_COLORS[autonomy] || S[200]}22`,
                      }}>{autonomy.replace(/_/g, ' ')}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-block', padding: '4px 10px', borderRadius: 6,
                        fontSize: 11, fontWeight: 700,
                        background: RISK_BG[profile.riskLevel] || S[100],
                        color: RISK_COLORS[profile.riskLevel] || S[600],
                      }}>{profile.riskLevel || 'Unknown'}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
                      {profile.totalLifetimeEvaluations || profile.evaluationCount || 0}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{ color: bc > 0 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>{bc}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {profile.anomalyDetected && <AlertTriangle size={15} color="#ea580c" />}
                    </td>
                    <td style={tdStyle}>
                      {(profile.probationActive || profile.onProbation) && (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                          background: ACCENT.rose.bg, color: ACCENT.rose.text, border: `1px solid ${ACCENT.rose.border}`,
                        }}>
                          <Lock size={10} /> PROBATION
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* Expanded Detail - Credo.ai-inspired Trust Score Breakdown */}
                  {isExpanded && (() => {
                    // Calculate trust dimensions
                    const evalCount = profile.totalLifetimeEvaluations || profile.evaluationCount || 0;
                    const blocks = profile.totalLifetimeBlocks || 0;
                    const warnings = profile.totalLifetimeWarnings || 0;
                    const minEvalsForScoring = 10;
                    const rampThreshold = 50;

                    // 1. COMPLIANCE: (evals - weighted_violations) / evals × 100
                    const weightedViolations = blocks * 2 + warnings * 0.5;
                    const effectiveEvals = Math.max(1, evalCount - weightedViolations);
                    const complianceRaw = evalCount > 0 ? (effectiveEvals / evalCount) * 100 : 0;
                    const compliance = Math.min(100, Math.max(0, complianceRaw));

                    // 2. VOLUME: evals relative to minEvalsForScoring
                    const volume = Math.min(100, (evalCount / minEvalsForScoring) * 100);

                    // 3. TENURE: days since registration relative to 90-day ramp
                    // Assuming profile.createdAt or similar - fallback to estimating from first eval
                    const ageDays = profile.ageInDays || 30; // placeholder if not available
                    const tenure = Math.min(100, (ageDays / 90) * 100);

                    // 4. SEVERITY: Inverse of violation severity ratio
                    const severityRatio = evalCount > 0 ? (blocks + warnings * 0.5) / evalCount : 0;
                    const severity = Math.max(0, 100 - severityRatio * 100);

                    // 5. CONSISTENCY: Score stability (inverse of variance if trend data exists)
                    let consistency = 75;
                    if (profile.scoreTrend && profile.scoreTrend.length > 1) {
                      const trend = profile.scoreTrend;
                      const mean = trend.reduce((a, b) => a + b, 0) / trend.length;
                      const variance = trend.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / trend.length;
                      const stdDev = Math.sqrt(variance);
                      // Low variance = high consistency
                      consistency = Math.max(0, Math.min(100, 100 - stdDev * 2));
                    }

                    // Display score with ramp
                    const isExternal = profile.agentType !== 'NATIVE';
                    const initialScore = isExternal ? 50 : 75;
                    const rampFactor = Math.min(1, evalCount / rampThreshold);
                    const displayScore = initialScore + (score - initialScore) * rampFactor;

                    // Radar chart dimensions — use viewBox for label breathing room
                    const chartSize = 180;
                    const svgPadding = 100; // extra space for labels beyond the polygon
                    const svgSize = chartSize + svgPadding * 2;
                    const center = svgSize / 2;
                    const maxRadius = chartSize / 2 - 10;

                    const dimensions = [
                      { name: 'Compliance', value: compliance, angle: -Math.PI / 2 },
                      { name: 'Volume', value: volume, angle: -Math.PI / 2 + (2 * Math.PI / 5) },
                      { name: 'Tenure', value: tenure, angle: -Math.PI / 2 + (4 * Math.PI / 5) },
                      { name: 'Severity', value: severity, angle: -Math.PI / 2 + (6 * Math.PI / 5) },
                      { name: 'Consistency', value: consistency, angle: -Math.PI / 2 + (8 * Math.PI / 5) },
                    ];

                    const getRadarPoint = (dimension, radius) => {
                      const x = center + radius * Math.cos(dimension.angle);
                      const y = center + radius * Math.sin(dimension.angle);
                      return [x, y];
                    };

                    const polygonPoints = dimensions.map(d => getRadarPoint(d, (d.value / 100) * maxRadius)).join(' ');

                    return (
                      <tr style={{ background: '#fdf6f5' }}>
                        <td colSpan="9" style={{ padding: '24px 20px 24px 52px', borderBottom: `1px solid ${S[200]}` }}>
                          {/* Main Grid: Radar + Formula */}
                          <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 32, marginBottom: 32, alignItems: 'start' }}>
                            {/* Radar Chart */}
                            <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 10, padding: '16px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, overflow: 'visible' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', alignSelf: 'flex-start' }}>Trust Dimensions</div>
                              <svg width="100%" viewBox={`0 0 ${svgSize} ${svgSize}`} style={{ overflow: 'visible' }}>
                                {/* Grid circles */}
                                {[0.25, 0.5, 0.75, 1].map((r) => (
                                  <circle key={r} cx={center} cy={center} r={r * maxRadius} fill="none" stroke={S[200]} strokeWidth="1" opacity="0.5" />
                                ))}
                                {/* Grid lines to vertices */}
                                {dimensions.map((d, i) => {
                                  const [x, y] = getRadarPoint(d, maxRadius);
                                  return <line key={i} x1={center} y1={center} x2={x} y2={y} stroke={S[200]} strokeWidth="1" opacity="0.3" />;
                                })}
                                {/* Data polygon */}
                                <polygon points={polygonPoints} fill={BRAND.primary} fillOpacity="0.15" stroke={BRAND.primary} strokeWidth="2" />
                                {/* Vertex labels — positioned outside polygon with dynamic anchoring */}
                                {dimensions.map((d, i) => {
                                  const labelDist = maxRadius + 50;
                                  const [x, y] = getRadarPoint(d, labelDist);
                                  // Smart text-anchor: left-side labels anchor end, right-side anchor start, top/center anchor middle
                                  const anchor = Math.abs(x - center) < 10 ? 'middle'
                                               : x < center ? 'end' : 'start';
                                  // Nudge top label up, bottom labels down for breathing room
                                  const yOffset = y < center - maxRadius * 0.3 ? -8
                                                : y > center + maxRadius * 0.3 ? 8 : 0;
                                  return (
                                    <g key={i}>
                                      <text x={x} y={y + yOffset} textAnchor={anchor} fontSize="12" fontWeight="600" fill={S[700]} dominantBaseline="middle">
                                        {d.name}
                                      </text>
                                      <text x={x} y={y + yOffset + 15} textAnchor={anchor} fontSize="11" fontWeight="700" fill={BRAND.primary} dominantBaseline="middle">
                                        {Math.round(d.value)}
                                      </text>
                                    </g>
                                  );
                                })}
                              </svg>
                            </div>

                            {/* Formula Breakdown */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                              {/* Core Formula */}
                              <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 8, padding: 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>Compliance Score Formula</div>
                                <div style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6, color: S[800], background: S[50], padding: 12, borderRadius: 4, marginBottom: 12 }}>
                                  <div>score = (effectiveEvals - weightedViolations) / effectiveEvals × 100</div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
                                  <div>
                                    <span style={{ color: S[500], fontWeight: 600 }}>effectiveEvals:</span>
                                    <div style={{ fontFamily: 'monospace', color: S[800], fontWeight: 700, marginTop: 2 }}>{Math.round(effectiveEvals)}</div>
                                  </div>
                                  <div>
                                    <span style={{ color: S[500], fontWeight: 600 }}>totalEvaluations:</span>
                                    <div style={{ fontFamily: 'monospace', color: S[800], fontWeight: 700, marginTop: 2 }}>{evalCount}</div>
                                  </div>
                                  <div>
                                    <span style={{ color: S[500], fontWeight: 600 }}>weightedViolations:</span>
                                    <div style={{ fontFamily: 'monospace', color: S[800], fontWeight: 700, marginTop: 2 }}>{Math.round(weightedViolations * 10) / 10}</div>
                                  </div>
                                  <div>
                                    <span style={{ color: S[500], fontWeight: 600 }}>blocks × 2 + warns × 0.5:</span>
                                    <div style={{ fontFamily: 'monospace', color: S[800], fontWeight: 700, marginTop: 2 }}>({blocks} × 2) + ({warnings} × 0.5)</div>
                                  </div>
                                </div>
                              </div>

                              {/* Ramp Factor */}
                              <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 8, padding: 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>Ramp Factor (Trust Maturity)</div>
                                <div style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6, color: S[800], background: S[50], padding: 12, borderRadius: 4, marginBottom: 12 }}>
                                  <div>displayScore = initialScore + (calcScore - initialScore) × min(1, evalCount / rampThreshold)</div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
                                  <div>
                                    <span style={{ color: S[500], fontWeight: 600 }}>initialScore (agent type):</span>
                                    <div style={{ fontFamily: 'monospace', color: S[800], fontWeight: 700, marginTop: 2 }}>{initialScore} ({isExternal ? 'External' : 'Native'})</div>
                                  </div>
                                  <div>
                                    <span style={{ color: S[500], fontWeight: 600 }}>rampThreshold:</span>
                                    <div style={{ fontFamily: 'monospace', color: S[800], fontWeight: 700, marginTop: 2 }}>{rampThreshold} evals</div>
                                  </div>
                                  <div>
                                    <span style={{ color: S[500], fontWeight: 600 }}>rampFactor:</span>
                                    <div style={{ fontFamily: 'monospace', color: S[800], fontWeight: 700, marginTop: 2 }}>{(rampFactor * 100).toFixed(1)}%</div>
                                  </div>
                                  <div>
                                    <span style={{ color: S[500], fontWeight: 600 }}>displayScore:</span>
                                    <div style={{ fontFamily: 'monospace', color: BRAND.primary, fontWeight: 700, marginTop: 2 }}>{Math.round(displayScore)}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Score Components Cards */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
                            {/* Compliance Rate */}
                            <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 8, padding: 16 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Compliance Rate</div>
                              <div style={{ fontSize: 20, fontWeight: 700, color: getScoreColor(compliance), marginBottom: 8 }}>{Math.round(compliance)}%</div>
                              <div style={{ width: '100%', height: 6, background: S[100], borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${compliance}%`, height: '100%', background: getScoreColor(compliance), borderRadius: 3, transition: 'width 0.3s ease' }} />
                              </div>
                            </div>

                            {/* Volume/Coverage */}
                            <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 8, padding: 16 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Evaluation Coverage</div>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                                <span style={{ fontSize: 20, fontWeight: 700, color: getScoreColor(volume) }}>{Math.round(evalCount)}</span>
                                <span style={{ fontSize: 12, color: S[400] }}>of {rampThreshold} needed for full trust maturity</span>
                              </div>
                              <div style={{ width: '100%', height: 6, background: S[100], borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${Math.min(100, volume)}%`, height: '100%', background: getScoreColor(volume), borderRadius: 3, transition: 'width 0.3s ease' }} />
                              </div>
                            </div>

                            {/* Violation Breakdown */}
                            <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 8, padding: 16 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Violations</div>
                              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#dc2626' }} />
                                  <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>{blocks}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#d97706' }} />
                                  <span style={{ fontSize: 12, fontWeight: 700, color: '#d97706' }}>{warnings}</span>
                                </div>
                              </div>
                            </div>

                            {/* Trust Trajectory */}
                            {(() => {
                              // Compute trajectory from actual data
                              const changeRate = profile.scoreChangeRate != null ? profile.scoreChangeRate
                                : profile.previousScore != null ? (score - profile.previousScore) : 0;
                              // If score is very low (below restricted threshold) and blocks dominate, it's declining
                              const isLowScore = score < 50;
                              const highBlockRatio = evalCount > 0 && (blocks / evalCount) > 0.5;
                              const trajectory = (isLowScore && highBlockRatio) ? 'Declining'
                                : changeRate > 1 ? 'Improving'
                                : changeRate < -1 ? 'Declining'
                                : 'Stable';
                              const tColor = trajectory === 'Improving' ? '#16a34a' : trajectory === 'Declining' ? '#dc2626' : '#d97706';
                              const TIcon = trajectory === 'Improving' ? ArrowUpRight : trajectory === 'Declining' ? ArrowDownRight : Activity;
                              return (
                                <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 8, padding: 16 }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Trajectory</div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <TIcon size={16} color={tColor} />
                                    <span style={{ fontSize: 13, fontWeight: 700, color: S[800] }}>{trajectory}</span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Score Trend Sparkline & Last Evaluated */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Score Trend (Last 30 Days)</div>
                              <Sparkline data={profile.scoreTrend || []} width={240} height={50} color={getScoreColor(score)} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Last Evaluated</div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: S[800] }}>
                                {(profile.lastEvaluatedAt || profile.lastEvaluated) ? new Date(profile.lastEvaluatedAt || profile.lastEvaluated).toLocaleDateString() : 'Never'}
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 8, borderTop: `1px solid ${S[200]}` }}>
                            <RefreshButton onClick={() => onRecalculate(profile.agentId)} />
                            {canModify && (
                              <>
                                {(profile.probationActive || profile.onProbation) ? (
                                  <button onClick={() => onRelease(profile.agentId)} style={{
                                    ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                                    background: ACCENT.emerald.icon, color: '#fff', borderRadius: 8,
                                    boxShadow: '0 1px 4px rgba(16,185,129,0.2)',
                                  }}>Release Probation</button>
                                ) : (
                                  <button onClick={() => onProbation(profile.agentId)} style={{
                                    ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                                    background: ACCENT.amber.icon, color: '#fff', borderRadius: 8,
                                    boxShadow: '0 1px 4px rgba(245,158,11,0.2)',
                                  }}>Trigger Probation</button>
                                )}
                                <PrimaryButton onClick={() => onOverride(profile.agentId, autonomy)} icon={Shield}>Manual Override</PrimaryButton>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </TCCard>
      <Pagination totalItems={filtered.length} page={pag.page} pageSize={pag.pageSize}
        onPageChange={pag.setPage} onPageSizeChange={pag.setPageSize} noun="agents" />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   TAB 3: ANOMALIES & ALERTS
   ═══════════════════════════════════════════════════════════════════════════ */
const AnomaliesTab = ({ alerts, loading, onProbation, onOverride, canModify }) => {
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {}, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: S[400] }}>Loading alerts...</div>;

  const selectedData = selectedAlert ? (alerts || []).find((a) => (a.id || a.agentId) === selectedAlert) : null;

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
          <span style={{ fontSize: 12, fontWeight: 600, color: S[600] }}>Auto-refresh (30s)</span>
        </label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: 16 }}>
        {/* Alerts List */}
        <TCCard style={{ padding: 0, maxHeight: 600, overflowY: 'auto' }}>
          {(alerts || []).length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14, background: ACCENT.emerald.bg,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
              }}>
                <CheckCircle size={22} color={ACCENT.emerald.icon} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: S[800] }}>All Clear</div>
              <div style={{ fontSize: 12, color: S[400], marginTop: 4 }}>No active anomalies detected</div>
            </div>
          ) : (
            (alerts || []).map((alert) => {
              const alertKey = alert.id || alert.agentId;
              const detectedAt = alert.anomalyDetectedAt || alert.detectionTime;
              const confidence = alert.anomalyConfidence ?? alert.confidence ?? 0;
              const confPct = parseFloat(confidence) > 1 ? parseFloat(confidence) : parseFloat(confidence) * 100;
              return (
                <div key={alertKey} onClick={() => setSelectedAlert(alertKey)} style={{
                  padding: '14px 18px', borderBottom: `1px solid ${S[100]}`, cursor: 'pointer',
                  background: selectedAlert === alertKey ? BRAND.light : '#fff',
                  borderLeft: selectedAlert === alertKey ? `3px solid ${BRAND.primary}` : '3px solid transparent',
                  transition: 'all 0.15s ease',
                }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: S[900], fontSize: 13 }}>{alert.agentName || 'Unknown'}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4,
                      fontSize: 10, fontWeight: 700, background: `${ANOMALY_COLORS[alert.anomalyType] || S[300]}15`,
                      color: ANOMALY_COLORS[alert.anomalyType] || S[500],
                    }}>
                      <AlertTriangle size={10} />
                      {alert.anomalyType || 'UNKNOWN'}
                    </span>
                    <span style={{ fontSize: 11, color: S[400] }}>{Math.round(confPct)}% confidence</span>
                  </div>
                  <div style={{ fontSize: 11, color: S[400] }}>
                    {detectedAt ? new Date(detectedAt).toLocaleString() : 'No date'}
                  </div>
                </div>
              );
            })
          )}
        </TCCard>

        {/* Alert Detail */}
        <TCCard accent={selectedData ? ANOMALY_COLORS[selectedData.anomalyType] : undefined} style={{ padding: 24 }}>
          {selectedData ? (() => {
            const confidence = selectedData.anomalyConfidence ?? selectedData.confidence ?? 0;
            const confPct = parseFloat(confidence) > 1 ? parseFloat(confidence) : parseFloat(confidence) * 100;
            return (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, background: BRAND.light,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Shield size={18} color={BRAND.primary} />
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: S[900] }}>{selectedData.agentName || 'Unknown'}</div>
                </div>

                {/* Key Info Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
                  {[
                    { label: 'Trust Score', value: formatScore(selectedData.currentScore), color: getScoreColor(selectedData.currentScore) },
                    { label: 'Anomaly Confidence', value: `${Math.round(confPct)}%`, color: '#ea580c' },
                    { label: 'Risk Level', value: selectedData.riskLevel || 'UNKNOWN', color: RISK_COLORS[selectedData.riskLevel] || S[500] },
                  ].map((m, i) => (
                    <div key={i} style={{ padding: '14px 16px', background: S[50], borderRadius: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>{m.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: m.color }}>{m.value}</div>
                    </div>
                  ))}
                </div>

                {/* Details */}
                <div style={{
                  padding: '14px 16px', background: S[50], borderRadius: 10, fontSize: 13,
                  color: S[700], lineHeight: 1.6, marginBottom: 20,
                  borderLeft: `3px solid ${ANOMALY_COLORS[selectedData.anomalyType] || S[300]}`,
                }}>
                  <strong style={{ color: S[900] }}>{(selectedData.anomalyType || 'UNKNOWN').replace(/_/g, ' ')}</strong>
                  {selectedData.anomalyType === 'SCORE_DRIFT' && ' — Trust score is changing faster than expected.'}
                  {selectedData.anomalyType === 'BLOCK_SPIKE' && ' — Sudden increase in blocked actions detected.'}
                  {selectedData.anomalyType === 'UNUSUAL_PATTERN' && ' — Activity pattern differs from historical norm.'}
                  <br /><br />
                  Agent at <strong>{(selectedData.currentAutonomyLevel || '').replace(/_/g, ' ')}</strong> autonomy.
                  {selectedData.probationActive && <><br /><span style={{ color: '#dc2626', fontWeight: 700 }}>On probation{selectedData.probationReason ? `: ${selectedData.probationReason}` : '.'}</span></>}
                  <br />
                  Lifetime: <strong>{selectedData.totalLifetimeEvaluations || 0}</strong> evals |{' '}
                  <strong style={{ color: '#dc2626' }}>{selectedData.totalLifetimeBlocks || 0}</strong> blocks |{' '}
                  <strong style={{ color: '#d97706' }}>{selectedData.totalLifetimeWarnings || 0}</strong> warnings
                </div>

                {(selectedData.scoreHistory || []).length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', marginBottom: 8 }}>Score History</div>
                    <Sparkline data={selectedData.scoreHistory} width={300} height={60} color={getScoreColor(selectedData.currentScore)} />
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {canModify && (
                    <>
                      <button onClick={() => onProbation(selectedData.agentId)} style={{
                        ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                        background: ACCENT.amber.icon, color: '#fff', borderRadius: 8,
                      }}><Lock size={14} /> Trigger Probation</button>
                      <PrimaryButton onClick={() => onOverride(selectedData.agentId, selectedData.currentAutonomyLevel)} icon={Shield}>Override Autonomy</PrimaryButton>
                    </>
                  )}
                </div>
              </div>
            );
          })() : (
            <div style={{ padding: '48px 20px', textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: S[100], display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <Eye size={22} color={S[400]} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: S[800] }}>Select an Alert</div>
              <div style={{ fontSize: 12, color: S[400], marginTop: 4 }}>Choose an alert from the list to view details</div>
            </div>
          )}
        </TCCard>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   TAB 4: BOARD REPORT
   ═══════════════════════════════════════════════════════════════════════════ */
const PRIORITY_COLORS = { HIGH: '#dc2626', MEDIUM: '#d97706', LOW: '#2563eb', INFO: S[400] };
const PRIORITY_BG = { HIGH: ACCENT.rose.bg, MEDIUM: ACCENT.amber.bg, LOW: ACCENT.sky.bg, INFO: S[100] };
const HEALTH_COLORS = { EXCELLENT: '#16a34a', GOOD: '#65a30d', NEEDS_ATTENTION: '#d97706', AT_RISK: '#ea580c', CRITICAL: '#dc2626' };
const HEALTH_LABELS = { EXCELLENT: 'Excellent', GOOD: 'Good', NEEDS_ATTENTION: 'Needs Attention', AT_RISK: 'At Risk', CRITICAL: 'Critical' };

const TrustGauge = ({ score, target, size = 180 }) => {
  const pct = Math.min(100, Math.max(0, parseFloat(score) || 0));
  const targetPct = Math.min(100, Math.max(0, parseFloat(target) || 95));
  const radius = (size - 20) / 2;
  const cx = size / 2, cy = size / 2;
  const startAngle = -225, endAngle = 45;
  const totalArc = endAngle - startAngle;

  const polarToCartesian = (angle) => {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };
  const arcPath = (s, e) => {
    const ps = polarToCartesian(s), pe = polarToCartesian(e);
    return `M ${ps.x} ${ps.y} A ${radius} ${radius} 0 ${Math.abs(e - s) > 180 ? 1 : 0} 1 ${pe.x} ${pe.y}`;
  };

  const scoreAngle = startAngle + (pct / 100) * totalArc;
  const targetAngle = startAngle + (targetPct / 100) * totalArc;
  const targetPos = polarToCartesian(targetAngle);
  const gaugeColor = pct >= targetPct ? '#16a34a' : pct >= targetPct - 10 ? '#d97706' : '#dc2626';

  return (
    <svg width={size} height={size * 0.75} viewBox={`0 0 ${size} ${size * 0.85}`}>
      <path d={arcPath(startAngle, endAngle)} fill="none" stroke={S[200]} strokeWidth={12} strokeLinecap="round" />
      {pct > 0 && <path d={arcPath(startAngle, scoreAngle)} fill="none" stroke={gaugeColor} strokeWidth={12} strokeLinecap="round" />}
      <circle cx={targetPos.x} cy={targetPos.y} r={5} fill={S[900]} stroke="#fff" strokeWidth={2} />
      <text x={targetPos.x} y={targetPos.y - 10} textAnchor="middle" fontSize={9} fill={S[500]} fontWeight={600}>Target</text>
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize={36} fontWeight={800} fill={gaugeColor}>{Math.round(pct)}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize={11} fill={S[500]}>out of 100</text>
    </svg>
  );
};

// Single row inside the Export dropdown. Two-line layout: bold primary
// label + small muted descriptor. Hover lifts the background to the
// brand light tone. Defined here (not in /components) because it's a
// purely-local affordance for BoardReportTab.
const ExportMenuItem = ({ onClick, primary, secondary }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        padding: '8px 10px', borderRadius: 6,
        background: hover ? '#fafaf9' : 'transparent',
        border: 'none', cursor: 'pointer',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1c1917' }}>{primary}</div>
      <div style={{ fontSize: 11, color: '#78716c', marginTop: 1 }}>{secondary}</div>
    </button>
  );
};

const BoardReportTab = ({ report, loading, dateRange, onDateRangeChange, agentScope, isAgentExternal }) => {
  const [expandedAgent, setExpandedAgent] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────
  // All hooks must be called before any early return, otherwise React's
  // hook-order tracking throws "Rendered more hooks than during the previous
  // render" (error #310). The export-menu hooks (state + ref + outside-click
  // effect) USED to live below the early-return; that meant the first render
  // (loading=true / report=null) called fewer hooks than the second render
  // (report arrives), which is what triggered #310 on this page. Hoisted
  // here alongside expandedAgent + scopedFm so hook order is stable across
  // every render path.
  //
  // Three export formats:
  //   • docx → branded executive narrative (backend: BoardReportExporter)
  //   • pptx → branded board deck          (backend: BoardReportExporter)
  //   • json → raw report payload          (client-side, dev/integrations)
  // ─────────────────────────────────────────────────────────────────────────
  const [exportMenuOpen, setExportMenuOpen] = React.useState(false);
  const [exporting, setExporting] = React.useState(null); // null | 'docx' | 'pptx' | 'json'
  const exportMenuRef = React.useRef(null);

  // Close the menu when a click lands outside it.
  React.useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (!exportMenuRef.current) return;
      if (!exportMenuRef.current.contains(e.target)) setExportMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [exportMenuOpen]);

  const fm = report?.fleetMetrics || {};
  const allRiskAgents = report
    ? (Array.isArray(report.topRiskAgents)
        ? report.topRiskAgents
        : Object.values(report.topRiskAgents || {}))
    : [];
  // Filter by agent scope when viewing External or Native Trust Center
  const topRiskAgents = agentScope && isAgentExternal
    ? allRiskAgents.filter(a => {
        const agentId = a.agentId?.toString?.() || a.agentId;
        const isExt = isAgentExternal(agentId);
        // Also check agentType field as fallback
        const typeIsExternal = (a.agentType || '').toUpperCase() === 'EXTERNAL';
        const ext = isExt || typeIsExternal;
        return agentScope === 'external' ? ext : agentScope === 'native' ? !ext : true;
      })
    : allRiskAgents;

  // When viewing a scoped Trust Center (External/Native), recompute fleet metrics
  // from the filtered agent list so numbers are accurate for the scope
  const isScopedView = !!(agentScope && isAgentExternal);
  const scopedFm = useMemo(() => {
    if (!isScopedView) return fm;
    // Recompute metrics from topRiskAgents (already filtered by scope)
    const agents = topRiskAgents;
    const total = agents.length;
    if (total === 0) return { ...fm, totalAgents: 0, highRiskCount: 0, anomaliesDetected: 0, agentsInProbation: 0, averageScore: 0, riskBreakdown: {} };
    const scores = agents.map(a => parseFloat(a.trustScore) || 0);
    const avg = scores.reduce((s, v) => s + v, 0) / total;
    const highRisk = agents.filter(a => a.riskLevel === 'HIGH' || a.riskLevel === 'CRITICAL').length;
    const anomalies = agents.filter(a => a.anomalyDetected).length;
    const probation = agents.filter(a => a.probationActive || a.onProbation).length;
    const rb = {};
    agents.forEach(a => { const rl = a.riskLevel || 'LOW'; rb[rl] = (rb[rl] || 0) + 1; });
    return {
      ...fm, totalAgents: total, highRiskCount: highRisk, anomaliesDetected: anomalies,
      agentsInProbation: probation, averageScore: avg.toFixed(1), riskBreakdown: rb,
    };
  }, [isScopedView, fm, topRiskAgents]);

  // Safe to early-return now — every hook above has been called.
  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: S[400] }}>Generating report...</div>;
  if (!report) return <div style={{ padding: 48, textAlign: 'center', color: S[400] }}>No report data available.</div>;

  const goals = report.organizationalGoals || {};
  const thresholds = report.scoreThresholds || {};
  const riskDefs = report.riskDefinitions || {};
  const recommendations = Array.isArray(report.recommendations) ? report.recommendations : [];

  const activeFm = isScopedView ? scopedFm : fm;
  const avgScore = parseFloat(activeFm.averageScore) || 0;
  const targetScore = parseFloat(activeFm.targetScore || goals.targetTrustScore) || 95;
  const meetsTarget = activeFm.meetsTarget === true || avgScore >= targetScore;
  const healthGrade = activeFm.healthGrade || (avgScore >= targetScore ? 'HEALTHY' : avgScore >= targetScore - 10 ? 'FAIR' : 'NEEDS_ATTENTION');
  const scoreGap = parseFloat(activeFm.scoreGap) || (targetScore - avgScore);
  const riskBreakdown = activeFm.riskBreakdown || {};

  // The dropdown ("[Export ▾]") opens on click and dispatches to the
  // right path. JSON stays for engineers / integrations; DOCX & PPTX
  // are the executive-grade outputs the user asked for. Hooks driving
  // exportMenuOpen / exporting / exportMenuRef were hoisted above the
  // early-return — see the top of this component.
  const handleExport = async (format) => {
    setExportMenuOpen(false);
    setExporting(format);
    try {
      if (format === 'json') {
        const json = JSON.stringify(report, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `agentlets-board-trust-report-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        return;
      }
      const { downloadTrustBoardReport } = await import('../services/api');
      const result = await downloadTrustBoardReport(format, { dateRange });
      if (!result?.ok) {
        // Surface the failure inline — toast plumbing isn't passed into
        // BoardReportTab as a prop, and we don't want to bubble a
        // service-layer dependency just for an error path.
        // eslint-disable-next-line no-alert
        window.alert(`Export failed: ${result?.error || 'Unknown error'}`);
      }
    } finally {
      setExporting(null);
    }
  };

  return (
    <div>
      {/* Header */}
      <TCCard style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 24 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: S[900], marginBottom: 4 }}>AI Agent Fleet — Board Trust Report</div>
          <div style={{ fontSize: 12, color: S[500] }}>
            Generated {new Date(report.reportGeneratedAt || Date.now()).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={dateRange} onChange={(e) => onDateRangeChange(e.target.value)} style={selectStyle}>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <div ref={exportMenuRef} style={{ position: 'relative' }}>
            <PrimaryButton
              onClick={() => setExportMenuOpen((v) => !v)}
              icon={Download}
              disabled={exporting != null}
            >
              {exporting ? `Generating ${exporting.toUpperCase()}…` : 'Export'}
              <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.85 }}>▾</span>
            </PrimaryButton>
            {exportMenuOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                background: '#ffffff', border: '1px solid #e7e5e4', borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 6,
                minWidth: 240, zIndex: 50,
              }}>
                <ExportMenuItem
                  onClick={() => handleExport('docx')}
                  primary="Word document (.docx)"
                  secondary="Executive narrative · branded"
                />
                <ExportMenuItem
                  onClick={() => handleExport('pptx')}
                  primary="PowerPoint deck (.pptx)"
                  secondary="Board-ready slides · 16:9 widescreen"
                />
                <div style={{ height: 1, background: '#e7e5e4', margin: '4px 6px' }} />
                <ExportMenuItem
                  onClick={() => handleExport('json')}
                  primary="JSON (raw)"
                  secondary="Integration / developer use"
                />
              </div>
            )}
          </div>
        </div>
      </TCCard>

      {/* Board Note */}
      {goals.boardReportNote && (
        <TCCard accent={ACCENT.amber.icon} style={{ background: ACCENT.amber.bg, borderColor: ACCENT.amber.border, marginBottom: 20, padding: '16px 20px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT.amber.text, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Organization Note</div>
          <div style={{ fontSize: 13, color: '#78350f', lineHeight: 1.5 }}>{goals.boardReportNote}</div>
        </TCCard>
      )}

      {/* Gauge + Quick Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20, marginBottom: 20 }}>
        <TCCard accent={HEALTH_COLORS[healthGrade]} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
          <TrustGauge score={avgScore} target={targetScore} />
          <div style={{ fontSize: 12, fontWeight: 700, color: HEALTH_COLORS[healthGrade] || S[500], textTransform: 'uppercase', marginTop: 4 }}>
            Fleet Health: {HEALTH_LABELS[healthGrade] || healthGrade}
          </div>
          <div style={{ fontSize: 11, color: S[400], marginTop: 4 }}>
            Target: {targetScore} &bull; Gap: {meetsTarget ? 'None' : scoreGap.toFixed(1) + ' pts'}
          </div>
        </TCCard>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { label: 'Total Agents', value: activeFm.totalAgents || 0, color: S[900], icon: Shield, accent: ACCENT.slate },
            { label: 'High Risk', value: activeFm.highRiskCount || 0, color: '#dc2626', icon: AlertTriangle, accent: ACCENT.rose },
            { label: 'Anomalies', value: activeFm.anomaliesDetected || 0, color: activeFm.anomaliesDetected > 0 ? '#ea580c' : '#16a34a', icon: Activity, accent: activeFm.anomaliesDetected > 0 ? ACCENT.amber : ACCENT.emerald },
            { label: 'On Probation', value: activeFm.agentsInProbation || 0, color: activeFm.agentsInProbation > 0 ? '#dc2626' : '#16a34a', icon: Lock, accent: activeFm.agentsInProbation > 0 ? ACCENT.rose : ACCENT.emerald },
            { label: 'Meets Target', value: meetsTarget ? 'YES' : 'NO', color: meetsTarget ? '#16a34a' : '#dc2626', icon: meetsTarget ? CheckCircle : AlertCircle, accent: meetsTarget ? ACCENT.emerald : ACCENT.rose },
            { label: 'Risk Tolerance', value: (goals.orgRiskTolerance || 'MODERATE').charAt(0) + (goals.orgRiskTolerance || 'MODERATE').slice(1).toLowerCase(), color: S[700], icon: Eye, accent: ACCENT.slate },
          ].map((m, i) => (
            <div key={i} style={{
              background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 10, padding: '14px 14px',
              borderTop: `3px solid ${m.accent.icon}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: S[500], marginBottom: 8 }}>
                <m.icon size={14} />
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{m.label}</span>
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: m.color }}>{m.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Executive Summary */}
      <TCCard accent={HEALTH_COLORS[healthGrade]} style={{ padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <BarChart3 size={18} color={BRAND.primary} />
          <span style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>Executive Summary</span>
        </div>
        {(typeof report.executiveSummary === 'string' ? report.executiveSummary : fm.healthExplanation || '').split('\n\n').map((para, i) => (
          <div key={i} style={{ fontSize: 13, color: S[700], lineHeight: 1.7, marginBottom: 12 }}>
            {para.startsWith('WHAT THE TRUST SCORE MEANS:') ? (
              <><span style={{ fontWeight: 700, color: S[900] }}>What the Trust Score Means: </span>{para.replace('WHAT THE TRUST SCORE MEANS: ', '')}</>
            ) : para.startsWith('CURRENT STATE:') ? (
              <><span style={{ fontWeight: 700, color: S[900] }}>Current State: </span>{para.replace('CURRENT STATE: ', '')}</>
            ) : para.startsWith('COMPLIANCE DEADLINE:') ? (
              <span style={{ fontWeight: 700, color: '#92400e' }}>{para}</span>
            ) : para}
          </div>
        ))}
      </TCCard>

      {/* Org Goals */}
      <TCCard style={{ marginBottom: 20, padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: S[900], marginBottom: 16 }}>Organizational Trust Goals</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {[
            { label: 'Target Score', value: targetScore, color: meetsTarget ? '#16a34a' : '#dc2626', sub: `Current: ${avgScore.toFixed(1)}` },
            { label: 'Target Autonomy', value: (goals.targetAutonomyLevel || 'SUPERVISED').replace(/_/g, ' '), color: S[900] },
            { label: 'Risk Tolerance', value: goals.orgRiskTolerance || 'MODERATE', color: S[900] },
            { label: 'Compliance Deadline', value: goals.complianceDeadline || 'Not set', color: goals.complianceDeadline ? '#92400e' : S[400] },
          ].map((g, i) => (
            <div key={i}>
              <div style={{ fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>{g.label}</div>
              <div style={{ fontSize: g.label === 'Target Score' ? 22 : 14, fontWeight: 800, color: g.color }}>{g.value}</div>
              {g.sub && <div style={{ fontSize: 11, color: S[400], marginTop: 2 }}>{g.sub}</div>}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: S[500], marginBottom: 4 }}>
            <span>Progress toward target</span>
            <span>{avgScore.toFixed(1)} / {targetScore}</span>
          </div>
          <div style={{ height: 10, background: S[100], borderRadius: 6, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(100, (avgScore / targetScore) * 100)}%`, height: '100%', borderRadius: 6,
              background: meetsTarget ? '#16a34a' : avgScore >= targetScore - 10 ? '#d97706' : '#dc2626',
              transition: 'width 0.5s ease',
            }} />
          </div>
        </div>
      </TCCard>

      {/* Risk Distribution */}
      <TCCard style={{ marginBottom: 20, padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: S[900], marginBottom: 16 }}>Agent Risk Distribution</div>
        <div style={{ display: 'flex', gap: 16 }}>
          {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(level => {
            const count = riskBreakdown[level] || 0;
            const total = activeFm.totalAgents || 1;
            const pct = ((count / total) * 100).toFixed(0);
            const def = riskDefs[level] || {};
            return (
              <div key={level} style={{
                flex: 1, padding: 16, borderRadius: 10,
                background: RISK_BG[level], border: `1px solid ${RISK_COLORS[level]}22`,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: RISK_COLORS[level], textTransform: 'uppercase', marginBottom: 6 }}>{def.label || level}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: RISK_COLORS[level], marginBottom: 2 }}>{count}</div>
                <div style={{ fontSize: 11, color: S[500] }}>{pct}% of fleet</div>
                <div style={{ fontSize: 11, color: S[600], marginTop: 8, lineHeight: 1.5 }}>{def.meaning || ''}</div>
              </div>
            );
          })}
        </div>
      </TCCard>

      {/* Agents Requiring Attention */}
      <TCCard style={{ marginBottom: 20, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <AlertTriangle size={18} color="#dc2626" />
          <span style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>Agents Requiring Attention</span>
        </div>
        {topRiskAgents.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <CheckCircle size={24} color="#16a34a" style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: '#16a34a' }}>All agents within acceptable parameters</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {topRiskAgents.map((agent, idx) => {
              const score = parseFloat(agent.trustScore) || 0;
              const gap = parseFloat(agent.scoreGap) || 0;
              const isExpanded = expandedAgent === idx;
              const remediations = Array.isArray(agent.remediations) ? agent.remediations : [];
              return (
                <div key={idx} style={{
                  border: `1px solid ${RISK_COLORS[agent.riskLevel] || S[200]}`,
                  borderRadius: 10, overflow: 'hidden', background: '#fff',
                }}>
                  <div onClick={() => setExpandedAgent(isExpanded ? null : idx)} style={{
                    padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16,
                    background: `${RISK_COLORS[agent.riskLevel] || S[100]}08`,
                  }}>
                    <span style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                      background: RISK_BG[agent.riskLevel] || S[100],
                      color: RISK_COLORS[agent.riskLevel] || S[600],
                      border: `1px solid ${RISK_COLORS[agent.riskLevel] || S[200]}33`,
                      textTransform: 'uppercase', minWidth: 60, textAlign: 'center',
                    }}>{agent.riskLevel}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: S[900] }}>{agent.agentName || 'Unknown'}</div>
                      <div style={{ fontSize: 11, color: S[500] }}>
                        {agent.agentType || 'Unknown'} &bull; {(agent.autonomyLevel || '').replace(/_/g, ' ')} &bull; {agent.agentStatus || ''}
                      </div>
                    </div>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 8,
                      fontSize: 16, fontWeight: 800, background: getScoreAccent(score).bg, color: getScoreColor(score),
                    }}>
                      <Shield size={13} /> {Math.round(score)}
                    </span>
                    <div style={{ display: 'flex', gap: 12, fontSize: 11, color: S[600] }}>
                      <div><strong style={{ color: '#dc2626' }}>{agent.totalBlocks || 0}</strong> blk</div>
                      <div><strong style={{ color: '#d97706' }}>{agent.totalWarnings || 0}</strong> wrn</div>
                      <div><strong>{agent.totalEvaluations || 0}</strong> eval</div>
                    </div>
                    {isExpanded ? <ChevronUp size={16} color={S[400]} /> : <ChevronDown size={16} color={S[400]} />}
                  </div>
                  {isExpanded && (
                    <div style={{ padding: '0 18px 18px' }}>
                      {agent.plainEnglish && (
                        <div style={{ background: S[50], border: `1px solid ${S[200]}`, borderRadius: 8, padding: 14, marginTop: 12, fontSize: 13, color: S[700], lineHeight: 1.6 }}>
                          {agent.plainEnglish}
                        </div>
                      )}
                      {agent.businessRisk && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', marginBottom: 6 }}>Business Risk</div>
                          <div style={{
                            fontSize: 13, color: S[700], lineHeight: 1.6, padding: 12, borderRadius: 8,
                            background: `${RISK_COLORS[agent.riskLevel]}08`,
                            borderLeft: `3px solid ${RISK_COLORS[agent.riskLevel]}`,
                          }}>{agent.businessRisk}</div>
                        </div>
                      )}
                      {remediations.length > 0 && (
                        <div style={{ marginTop: 14 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', marginBottom: 8 }}>Recommended Actions</div>
                          {remediations.map((r, ri) => (
                            <div key={ri} style={{
                              display: 'flex', gap: 12, padding: '10px 12px', marginBottom: 6, borderRadius: 8,
                              background: PRIORITY_BG[r.priority] || S[100], border: `1px solid ${PRIORITY_COLORS[r.priority] || S[200]}22`,
                            }}>
                              <span style={{
                                fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, height: 'fit-content', marginTop: 2,
                                background: PRIORITY_COLORS[r.priority] || S[400], color: '#fff', textTransform: 'uppercase',
                              }}>{r.priority}</span>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: S[900], marginBottom: 2 }}>{r.action}</div>
                                <div style={{ fontSize: 12, color: S[600], lineHeight: 1.5 }}>{r.detail}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                        {agent.anomalyDetected && (
                          <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: ACCENT.rose.bg, color: ACCENT.rose.text, fontWeight: 600, border: `1px solid ${ACCENT.rose.border}` }}>
                            Anomaly: {(agent.anomalyType || '').replace(/_/g, ' ')}
                          </span>
                        )}
                        {agent.probationActive && (
                          <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: ACCENT.rose.bg, color: ACCENT.rose.text, fontWeight: 600, border: `1px solid ${ACCENT.rose.border}` }}>
                            On Probation
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </TCCard>

      {/* Recommendations */}
      <TCCard style={{ marginBottom: 20, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Zap size={18} color={ACCENT.amber.icon} />
          <span style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>Fleet-Level Recommendations</span>
        </div>
        {recommendations.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#16a34a', fontWeight: 700 }}>
            Fleet in good standing. No actionable recommendations.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {recommendations.map((rec, idx) => (
              <div key={idx} style={{
                display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 8,
                background: PRIORITY_BG[rec.priority] || S[100], border: `1px solid ${PRIORITY_COLORS[rec.priority] || S[200]}22`,
              }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, height: 'fit-content', marginTop: 2,
                  background: PRIORITY_COLORS[rec.priority] || S[400], color: '#fff', textTransform: 'uppercase',
                }}>{rec.priority}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: S[900], marginBottom: 2 }}>{rec.category}</div>
                  <div style={{ fontSize: 12, color: S[600], lineHeight: 1.5 }}>{rec.recommendation}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </TCCard>

      {/* Risk Level Definitions */}
      <TCCard style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={18} color={BRAND.primary} />
          <span style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>How We Define Risk Levels</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...thStyle, width: 130 }}>Level</th>
            <th style={thStyle}>What It Means</th>
            <th style={thStyle}>Recommended Action</th>
          </tr></thead>
          <tbody>
            {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(level => {
              const def = riskDefs[level] || {};
              return (
                <tr key={level} className="tc-row">
                  <td style={{ ...tdStyle, verticalAlign: 'middle' }}>
                    <span style={{
                      display: 'inline-block', padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                      background: RISK_BG[level], color: RISK_COLORS[level], whiteSpace: 'nowrap', textAlign: 'center',
                    }}>{def.label || level}</span>
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, lineHeight: 1.5, verticalAlign: 'middle' }}>{def.meaning || '—'}</td>
                  <td style={{ ...tdStyle, fontSize: 12, lineHeight: 1.5, verticalAlign: 'middle' }}>{def.action || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TCCard>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   TAB 5: SCORING CONFIGURATION (Scoped Inheritance)
   ═══════════════════════════════════════════════════════════════════════════ */

const SCOPE_META = {
  ORG:    { label: 'Organization', color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE', icon: Users, desc: 'Default for all tenants and agents in the org' },
  TENANT: { label: 'Tenant',       color: '#0EA5E9', bg: '#F0F9FF', border: '#BAE6FD', icon: Shield, desc: 'Overrides org defaults for this tenant' },
  AGENT:  { label: 'Agent',        color: '#F59E0B', bg: '#FFFBEB', border: '#FDE68A', icon: Target, desc: 'Overrides tenant defaults for a specific agent' },
};

const ScoringConfigTab = ({ onSaved, canEditGoals = false, userRole = 'Viewer', userOrgId = null, userTenantId = null }) => {
  const [activeScope, setActiveScope] = useState('TENANT');
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [config, setConfig] = useState(null);
  const [allConfigs, setAllConfigs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [isPersisted, setIsPersisted] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { addToast } = useToast();

  // ── RBAC-aware Org / Tenant / Agent selectors ──
  const [orgs, setOrgs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(userOrgId);
  const [selectedTenantId, setSelectedTenantId] = useState(userTenantId);

  const isSuperAdminRole = userRole === 'Super Admin' || userRole === 'super_admin' || userRole === 'admin';
  const isOrgAdminRole = isSuperAdminRole || userRole === 'Org Admin' || userRole === 'org_admin';
  const isTenantAdminRole = isOrgAdminRole || userRole === 'Tenant Admin' || userRole === 'tenant_admin';

  // Load organizations on mount (Super Admin: all, Org Admin: only their own)
  useEffect(() => {
    (async () => {
      const res = await api.getOrganizations();
      if (res.ok) {
        const allOrgs = res.data || [];
        if (isSuperAdminRole) {
          setOrgs(allOrgs);
        } else {
          // Org Admin / Tenant Admin: filter to their own org
          setOrgs(allOrgs.filter(o => o.id === userOrgId));
        }
        // Auto-select user's org if available
        if (userOrgId && allOrgs.find(o => o.id === userOrgId)) {
          setSelectedOrgId(userOrgId);
        } else if (allOrgs.length === 1) {
          setSelectedOrgId(allOrgs[0].id);
        }
      }
    })();
  }, [userOrgId, isSuperAdminRole]);

  // Load tenants when org changes
  useEffect(() => {
    if (!selectedOrgId) { setTenants([]); return; }
    (async () => {
      const res = await api.getTenantsByOrg(selectedOrgId);
      if (res.ok) {
        const allTenants = res.data || [];
        if (isSuperAdminRole || isOrgAdminRole) {
          setTenants(allTenants);
        } else {
          // Tenant Admin: filter to their own tenant(s)
          setTenants(allTenants.filter(t => t.id === userTenantId));
        }
        // Auto-select user's tenant if it's in the list
        if (userTenantId && allTenants.find(t => t.id === userTenantId)) {
          setSelectedTenantId(userTenantId);
        } else if (allTenants.length === 1) {
          setSelectedTenantId(allTenants[0].id);
        } else {
          setSelectedTenantId(null);
        }
      }
    })();
  }, [selectedOrgId, userTenantId, isSuperAdminRole, isOrgAdminRole]);

  // Load agents filtered by selected tenant
  useEffect(() => {
    if (!selectedOrgId) return;
    (async () => {
      const res = await api.getAgents({ orgId: selectedOrgId, tenantId: selectedTenantId || undefined });
      if (res.ok) setAgents(res.data || []);
    })();
  }, [selectedOrgId, selectedTenantId]);

  // Load all existing configs + agents list on mount
  useEffect(() => {
    (async () => {
      setLoading(true);
      const configsRes = await api.listTrustScoringConfigs(selectedTenantId || undefined);
      if (configsRes.ok) setAllConfigs(configsRes.data || []);
      setLoading(false);
    })();
  }, [selectedOrgId, selectedTenantId]);

  // Load config whenever scope, org, tenant, or agent changes
  useEffect(() => {
    if (loading) return;
    // For ORG scope, need selectedOrgId; for TENANT/AGENT scope, need selectedTenantId
    if (activeScope === 'ORG' && !selectedOrgId) { setConfig(null); setIsPersisted(false); return; }
    if (activeScope === 'TENANT' && !selectedTenantId) { setConfig(null); setIsPersisted(false); return; }
    if (activeScope === 'AGENT' && !selectedAgentId) { setConfig(null); setIsPersisted(false); setDirty(false); return; }
    (async () => {
      const agentId = activeScope === 'AGENT' ? selectedAgentId : undefined;
      const tenantId = activeScope !== 'ORG' ? selectedTenantId : undefined;
      const res = await api.getTrustScoringConfig(tenantId, activeScope, agentId);
      if (res.ok) {
        setConfig(res.data);
        setIsPersisted(res.data.id != null);
        setDirty(false);
      } else {
        setConfig(null);
        setIsPersisted(false);
      }
    })();
  }, [activeScope, selectedAgentId, selectedOrgId, selectedTenantId, loading]);

  const update = (field, value) => { setConfig(prev => ({ ...prev, [field]: value })); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    const { id, createdAt, updatedAt, updatedBy, tenantName, orgName, ...payload } = config;
    payload.configScope = activeScope;
    if (activeScope === 'AGENT') payload.agentId = selectedAgentId;
    if (selectedOrgId) payload.orgId = selectedOrgId;
    if (selectedTenantId && activeScope !== 'ORG') payload.tenantId = selectedTenantId;
    const res = await api.updateTrustScoringConfig(payload);
    setSaving(false);
    if (res.ok) {
      setConfig(res.data);
      setIsPersisted(true);
      setDirty(false);
      addToast(`${SCOPE_META[activeScope].label} scoring config saved.`, 'success');
      const refreshed = await api.listTrustScoringConfigs(selectedTenantId || undefined);
      if (refreshed.ok) setAllConfigs(refreshed.data || []);
      if (onSaved) onSaved();
    } else {
      addToast('Failed to save: ' + (res.error || 'Unknown error'), 'error');
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    const agentId = activeScope === 'AGENT' ? selectedAgentId : undefined;
    const tenantId = activeScope !== 'ORG' ? selectedTenantId : undefined;
    const res = await api.deleteTrustScoringConfig(activeScope, tenantId, agentId);
    if (res.ok) {
      addToast(`${SCOPE_META[activeScope].label} config removed — now inheriting from parent.`, 'success');
      setIsPersisted(false);
      const reloaded = await api.getTrustScoringConfig(tenantId, activeScope, agentId);
      if (reloaded.ok) { setConfig(reloaded.data); setIsPersisted(reloaded.data.id != null); }
      setDirty(false);
      const refreshed = await api.listTrustScoringConfigs(selectedTenantId || undefined);
      if (refreshed.ok) setAllConfigs(refreshed.data || []);
      if (onSaved) onSaved();
    } else {
      addToast('Failed to delete config: ' + (res.error || 'Unknown error'), 'error');
    }
  };

  /* Discard unsaved edits and show the values this scope resolves to again.
   *
   * This used to overwrite the form with a table of numbers written here — among them a 30-day
   * scoring window against the platform's 90, and violation weights of 3 / 1 / 0.25 against
   * 3.00 / 0.00 / 0.00 — and mark the form dirty, so the next Save persisted them. The
   * effective configuration is the one source of these values. */
  const handleReset = async () => {
    const reloaded = await api.getTrustScoringConfig(tenantId, activeScope, agentId);
    if (!reloaded.ok) {
      addToast('Could not reload the configuration for this scope: '
               + (reloaded.error || 'Unknown error'), 'error');
      return;
    }
    setConfig(reloaded.data);
    setIsPersisted(reloaded.data.id != null);
    setDirty(false);
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: S[400] }}>Loading configuration...</div>;

  // Which scopes have persisted overrides
  const persistedScopes = new Set(allConfigs.map(c => c.configScope));
  const agentConfigs = allConfigs.filter(c => c.configScope === 'AGENT');

  const sectionCard = (accent) => ({
    background: '#ffffff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: 24, marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)', borderTop: `3px solid ${accent || BRAND.primary}`,
  });
  const fieldRow = { display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' };
  const fieldGroup = { flex: '1 1 200px', minWidth: 180 };
  const descStyle = { fontSize: 11, color: S[400], marginTop: 4, lineHeight: 1.4 };
  const numInput = { ...inputBase, width: 120, textAlign: 'right', fontWeight: 600, fontSize: 14, fontFamily: 'monospace' };

  const scopeMeta = SCOPE_META[activeScope];

  return (
    <div>
      {/* ── Inheritance Chain Visualization ── */}
      <div style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: '20px 24px',
        marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Layers size={18} color={BRAND.primary} />
          <span style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>Configuration Inheritance</span>
          <span style={{ fontSize: 11, color: S[400], marginLeft: 8 }}>
            Most-specific scope wins entirely. Select a scope to view or create an override.
          </span>
        </div>

        {/* Scope Tabs */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {['ORG', 'TENANT', 'AGENT'].map((scope) => {
            const meta = SCOPE_META[scope];
            const isActive = activeScope === scope;
            const hasPersisted = scope === 'AGENT' ? agentConfigs.length > 0 : persistedScopes.has(scope);
            return (
              <button key={scope} onClick={() => { setActiveScope(scope); setSelectedAgentId(null); }}
                style={{
                  ...btnBase, flex: 1, padding: '14px 16px', borderRadius: 10,
                  border: isActive ? `2px solid ${meta.color}` : `1px solid ${S[200]}`,
                  background: isActive ? meta.bg : '#fff',
                  cursor: 'pointer', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
                  transition: 'all 0.15s ease',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                  <meta.icon size={15} color={isActive ? meta.color : S[400]} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: isActive ? meta.color : S[700] }}>{meta.label}</span>
                  {hasPersisted && (
                    <span style={{
                      marginLeft: 'auto', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                      background: meta.color, color: '#fff',
                    }}>CUSTOM</span>
                  )}
                  {!hasPersisted && (
                    <span style={{
                      marginLeft: 'auto', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                      background: S[100], color: S[400],
                    }}>INHERITED</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: S[400], lineHeight: 1.3, textAlign: 'left' }}>{meta.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Inheritance Flow Arrow */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '0 20px' }}>
          {['ORG', 'TENANT', 'AGENT'].map((scope, i) => {
            const meta = SCOPE_META[scope];
            const isActive = activeScope === scope;
            const hasPersisted = scope === 'AGENT' ? agentConfigs.length > 0 : persistedScopes.has(scope);
            return (
              <React.Fragment key={scope}>
                <div style={{
                  width: 12, height: 12, borderRadius: '50%',
                  background: hasPersisted ? meta.color : S[200],
                  border: isActive ? `3px solid ${meta.color}` : 'none',
                  boxSizing: 'border-box',
                }} />
                {i < 2 && (
                  <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, ${SCOPE_META[['ORG','TENANT','AGENT'][i]].color}40, ${SCOPE_META[['ORG','TENANT','AGENT'][i+1]].color}40)` }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Org / Tenant / Agent Selectors (RBAC-aware) ── */}
        <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {/* Organization Selector — always shown */}
          <div style={{ flex: '1 1 220px', minWidth: 200 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: SCOPE_META.ORG.color, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <Users size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Organization
            </label>
            <select
              value={selectedOrgId || ''}
              onChange={(e) => { setSelectedOrgId(e.target.value || null); setSelectedTenantId(null); setSelectedAgentId(null); }}
              style={{ ...selectStyle, width: '100%', borderColor: SCOPE_META.ORG.border }}
              disabled={orgs.length <= 1}
            >
              {orgs.length === 0 && <option value="">Loading...</option>}
              {orgs.length === 1 ? (
                <option value={orgs[0].id}>{orgs[0].name}</option>
              ) : (
                <>
                  <option value="">— Select organization —</option>
                  {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </>
              )}
            </select>
            {!isSuperAdminRole && orgs.length === 1 && (
              <div style={{ fontSize: 10, color: S[400], marginTop: 4 }}>Restricted to your organization</div>
            )}
          </div>

          {/* Tenant Selector — shown for TENANT and AGENT scope */}
          {(activeScope === 'TENANT' || activeScope === 'AGENT') && (
            <div style={{ flex: '1 1 220px', minWidth: 200 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: SCOPE_META.TENANT.color, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <Shield size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Tenant
              </label>
              <select
                value={selectedTenantId || ''}
                onChange={(e) => { setSelectedTenantId(e.target.value || null); setSelectedAgentId(null); }}
                style={{ ...selectStyle, width: '100%', borderColor: SCOPE_META.TENANT.border }}
                disabled={!selectedOrgId || tenants.length <= 1}
              >
                {!selectedOrgId ? (
                  <option value="">Select an organization first</option>
                ) : tenants.length === 0 ? (
                  <option value="">No tenants found</option>
                ) : tenants.length === 1 ? (
                  <option value={tenants[0].id}>{tenants[0].name}</option>
                ) : (
                  <>
                    <option value="">— Select tenant —</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </>
                )}
              </select>
              {!isSuperAdminRole && !isOrgAdminRole && tenants.length === 1 && (
                <div style={{ fontSize: 10, color: S[400], marginTop: 4 }}>Restricted to your tenant</div>
              )}
            </div>
          )}

          {/* Agent Selector — shown for AGENT scope */}
          {activeScope === 'AGENT' && (
            <div style={{ flex: '1.5 1 280px', minWidth: 240 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: SCOPE_META.AGENT.color, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <Target size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Agent
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={selectedAgentId || ''}
                  onChange={(e) => setSelectedAgentId(e.target.value || null)}
                  style={{ ...selectStyle, flex: 1, minWidth: 200, borderColor: SCOPE_META.AGENT.border }}
                  disabled={!selectedTenantId}
                >
                  {!selectedTenantId ? (
                    <option value="">Select a tenant first</option>
                  ) : agents.length === 0 ? (
                    <option value="">No agents found</option>
                  ) : (
                    <>
                      <option value="">— Choose an agent —</option>
                      {agents.map(a => (
                        <option key={a.id} value={a.id}>{a.name || a.id}{a.type ? ` (${a.type})` : ''}</option>
                      ))}
                    </>
                  )}
                </select>
                {agentConfigs.length > 0 && (
                  <div style={{ fontSize: 11, color: S[500] }}>
                    {agentConfigs.length} override{agentConfigs.length !== 1 ? 's' : ''}:
                    {agentConfigs.slice(0, 3).map(ac => {
                      const agentName = agents.find(a => a.id === ac.agentId)?.name || ac.agentId?.substring(0, 8);
                      return (
                        <button key={ac.agentId} onClick={() => setSelectedAgentId(ac.agentId)}
                          style={{ ...btnBase, fontSize: 11, color: SCOPE_META.AGENT.color, fontWeight: 600, padding: '2px 6px', marginLeft: 4, background: 'transparent', textDecoration: 'underline', cursor: 'pointer' }}>
                          {agentName}
                        </button>
                      );
                    })}
                    {agentConfigs.length > 3 && <span> +{agentConfigs.length - 3} more</span>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Empty States: when required selectors are missing ── */}
      {activeScope === 'ORG' && !selectedOrgId && (
        <div style={{ padding: 48, textAlign: 'center', color: S[400] }}>
          <Users size={36} color={S[300]} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: S[500], marginBottom: 4 }}>Select an Organization</div>
          <div style={{ fontSize: 13 }}>Choose an organization above to view or create an org-level scoring config.</div>
        </div>
      )}
      {activeScope === 'TENANT' && !selectedTenantId && (
        <div style={{ padding: 48, textAlign: 'center', color: S[400] }}>
          <Shield size={36} color={S[300]} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: S[500], marginBottom: 4 }}>Select a Tenant</div>
          <div style={{ fontSize: 13 }}>{!selectedOrgId ? 'Select an organization first, then choose a tenant.' : 'Choose a tenant above to view or create a tenant-level scoring config override.'}</div>
        </div>
      )}
      {activeScope === 'AGENT' && !selectedAgentId && (
        <div style={{ padding: 48, textAlign: 'center', color: S[400] }}>
          <Target size={36} color={S[300]} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: S[500], marginBottom: 4 }}>Select an Agent</div>
          <div style={{ fontSize: 13 }}>{!selectedTenantId ? 'Select an organization and tenant first, then choose an agent.' : 'Choose an agent above to view or create a scoring config override for it.'}</div>
        </div>
      )}

      {/* ── Config Editor ── */}
      {config && (
        (activeScope === 'ORG' && selectedOrgId) ||
        (activeScope === 'TENANT' && selectedTenantId) ||
        (activeScope === 'AGENT' && selectedAgentId)
      ) && (() => {
        const exampleRuns = 10, exampleBlocks = 1, exampleWarns = 2, exampleAudits = 3;
        const effEvals = exampleRuns * (config.evaluationMultiplier || 1);
        const weighted = exampleBlocks * (config.blockWeight || 3) + exampleWarns * (config.warnWeight || 1) + exampleAudits * (config.auditWeight || 0.25);
        const previewScore = effEvals > 0 ? Math.max(0, ((effEvals - weighted) / effEvals * 100)).toFixed(2) : '100.00';

        return (
          <>
            {/* Status Banner */}
            <div style={{
              background: isPersisted ? scopeMeta.bg : '#FFFBEB',
              border: `1px solid ${isPersisted ? scopeMeta.border : '#FDE68A'}`,
              borderRadius: 10, padding: '12px 18px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: isPersisted ? scopeMeta.color : '#F59E0B' }} />
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: isPersisted ? scopeMeta.color : '#92400E', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {isPersisted ? `Custom ${scopeMeta.label} Override` : `Inherited Configuration (no ${scopeMeta.label.toLowerCase()} override)`}
                  </div>
                  <div style={{ fontSize: 12, color: S[500], marginTop: 2 }}>
                    {isPersisted
                      ? `This ${scopeMeta.label.toLowerCase()} has its own saved scoring config.`
                      : `Showing effective config inherited from parent scope. Save to create a ${scopeMeta.label.toLowerCase()}-level override.`
                    }
                  </div>
                </div>
              </div>
              {isPersisted && (
                <div style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: scopeMeta.color, color: '#fff' }}>
                  PERSISTED
                </div>
              )}
            </div>

            {/* Action Bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: S[900], marginBottom: 4 }}>
                  {scopeMeta.label} Scoring Configuration
                </div>
                <div style={{ fontSize: 13, color: S[500] }}>
                  {activeScope === 'ORG' && 'Default scoring weights and thresholds for all tenants and agents in this organization.'}
                  {activeScope === 'TENANT' && 'Scoring configuration for agents within this tenant. Overrides org defaults.'}
                  {activeScope === 'AGENT' && `Scoring override for this specific agent. Falls back to tenant config if removed.`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {isPersisted && (
                  <GhostButton onClick={() => setShowDeleteConfirm(true)} icon={Trash2}>Remove Override</GhostButton>
                )}
                {/* Re-reads this scope's effective configuration — see handleReset. */}
                <GhostButton onClick={handleReset} icon={RefreshCw}>Discard Edits</GhostButton>
                <PrimaryButton onClick={handleSave} disabled={!dirty || saving}>
                  {saving ? 'Saving...' : isPersisted ? 'Update' : `Create ${scopeMeta.label} Override`}
                </PrimaryButton>
              </div>
            </div>

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
              <div style={{
                background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: 16,
                marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <AlertTriangle size={18} color="#DC2626" />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#991B1B' }}>
                      Remove {scopeMeta.label} override?
                    </div>
                    <div style={{ fontSize: 12, color: '#B91C1C' }}>
                      This scope will inherit from its parent. This cannot be undone.
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <GhostButton onClick={() => setShowDeleteConfirm(false)}>Cancel</GhostButton>
                  <PrimaryButton onClick={handleDelete} style={{ background: '#DC2626' }}>
                    Yes, Remove
                  </PrimaryButton>
                </div>
              </div>
            )}

            {/* Evaluation Multiplier */}
            <div style={sectionCard(ACCENT.sky.icon)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Zap size={18} color={ACCENT.sky.icon} />
                <span style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>Evaluation Multiplier</span>
              </div>
              <p style={{ fontSize: 13, color: S[600], marginBottom: 16, lineHeight: 1.6 }}>
                Each enforcement event counts as this many evaluations in the trust formula.
              </p>
              <div style={fieldRow}>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Multiplier (1 run = N evaluations)</label>
                  <input type="number" min="1" max="100" step="1" value={config.evaluationMultiplier || 1}
                    onChange={(e) => update('evaluationMultiplier', Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))} style={numInput} />
                  <div style={descStyle}>Range: 1–100. Default: 1</div>
                </div>
                <div style={{ ...fieldGroup, maxWidth: 400 }}>
                  <label style={labelStyle}>Min Evaluations for Scoring</label>
                  <input type="number" min="1" max="10000" step="1" value={config.minEvalsForScoring || 50}
                    onChange={(e) => update('minEvalsForScoring', Math.max(1, parseInt(e.target.value) || 50))} style={numInput} />
                  <div style={descStyle}>Agents below stay RESTRICTED. Default: 50</div>
                </div>
              </div>
              <div style={{ background: BRAND.light, borderRadius: 8, padding: 16, marginTop: 8, border: `1px solid ${BRAND.soft}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.dark, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Live Preview</div>
                <div style={{ fontSize: 13, color: S[600], lineHeight: 1.8, fontFamily: 'monospace' }}>
                  <div>Scenario: {exampleRuns} runs, {exampleBlocks} BLOCK, {exampleWarns} WARNs, {exampleAudits} AUDITs</div>
                  <div>Effective evaluations: {exampleRuns} × {config.evaluationMultiplier || 1} = <strong>{effEvals}</strong></div>
                  <div>Weighted violations: {exampleBlocks}×{config.blockWeight || 3} + {exampleWarns}×{config.warnWeight || 1} + {exampleAudits}×{config.auditWeight || 0.25} = <strong>{weighted.toFixed(2)}</strong></div>
                  <div>Score: ({effEvals} - {weighted.toFixed(2)}) / {effEvals} × 100 = <strong style={{ fontSize: 16, color: BRAND.primary }}>{previewScore}%</strong></div>
                </div>
              </div>
            </div>

            {/* Violation Weights */}
            <div style={sectionCard(ACCENT.rose.icon)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <AlertTriangle size={18} color={ACCENT.rose.icon} />
                <span style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>Violation Weights</span>
              </div>
              <div style={fieldRow}>
                <div style={fieldGroup}>
                  <label style={labelStyle}>BLOCK Weight</label>
                  <input type="number" min="0" max="50" step="0.25" value={config.blockWeight ?? 3}
                    onChange={(e) => update('blockWeight', parseFloat(e.target.value) || 0)} style={{ ...numInput, borderLeft: '4px solid #dc2626' }} />
                  <div style={descStyle}>Most severe. Default: 3.00</div>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>WARN Weight</label>
                  <input type="number" min="0" max="50" step="0.25" value={config.warnWeight ?? 1}
                    onChange={(e) => update('warnWeight', parseFloat(e.target.value) || 0)} style={{ ...numInput, borderLeft: '4px solid #d97706' }} />
                  <div style={descStyle}>Moderate. Default: 1.00</div>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>AUDIT Weight</label>
                  <input type="number" min="0" max="50" step="0.05" value={config.auditWeight ?? 0.25}
                    onChange={(e) => update('auditWeight', parseFloat(e.target.value) || 0)} style={{ ...numInput, borderLeft: `4px solid ${S[400]}` }} />
                  <div style={descStyle}>Informational. Default: 0.25</div>
                </div>
              </div>
            </div>

            {/* Autonomy Thresholds */}
            <div style={sectionCard(BRAND.primary)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Shield size={18} color={BRAND.primary} />
                <span style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>Autonomy Level Thresholds</span>
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', height: 36, borderRadius: 8, overflow: 'hidden', border: `1px solid ${S[200]}` }}>
                  {[
                    { label: `PROBATION (<${config.restrictedThreshold || 75}%)`, flex: config.restrictedThreshold || 75, bg: S[500] },
                    { label: 'RESTRICTED', flex: (config.supervisedThreshold || 90) - (config.restrictedThreshold || 75), bg: S[400] },
                    { label: 'SUPERVISED', flex: (config.selfAutonomousThreshold || 98) - (config.supervisedThreshold || 90), bg: '#ea6b5e' },
                    { label: 'SELF AUTO', flex: 100 - (config.selfAutonomousThreshold || 98), bg: BRAND.primary },
                  ].map((seg, i) => (
                    <div key={i} style={{
                      flex: seg.flex, background: seg.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
                    }}>{seg.label}</div>
                  ))}
                </div>
              </div>
              <div style={fieldRow}>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Self-Autonomous (≥)</label>
                  <input type="number" min="50" max="100" step="0.5" value={config.selfAutonomousThreshold ?? 98}
                    onChange={(e) => update('selfAutonomousThreshold', parseFloat(e.target.value) || 98)}
                    style={{ ...numInput, borderLeft: `4px solid ${BRAND.primary}` }} />
                  <div style={descStyle}>Default: 98.00%</div>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Min Evals for Self-Autonomous</label>
                  <input type="number" min="10" max="10000" step="10" value={config.selfAutonomousMinEvals ?? 500}
                    onChange={(e) => update('selfAutonomousMinEvals', parseInt(e.target.value) || 500)} style={numInput} />
                  <div style={descStyle}>Default: 500</div>
                </div>
              </div>
              <div style={fieldRow}>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Supervised (≥)</label>
                  <input type="number" min="50" max="100" step="0.5" value={config.supervisedThreshold ?? 90}
                    onChange={(e) => update('supervisedThreshold', parseFloat(e.target.value) || 90)}
                    style={{ ...numInput, borderLeft: '4px solid #ea6b5e' }} />
                  <div style={descStyle}>Default: 90.00%</div>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Restricted (≥)</label>
                  <input type="number" min="0" max="100" step="0.5" value={config.restrictedThreshold ?? 75}
                    onChange={(e) => update('restrictedThreshold', parseFloat(e.target.value) || 75)}
                    style={{ ...numInput, borderLeft: `4px solid ${S[400]}` }} />
                  <div style={descStyle}>Below this = Probation. Default: 75.00%</div>
                </div>
              </div>
            </div>

            {/* Scoring Window & Probation */}
            <div style={sectionCard(ACCENT.amber.icon)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Clock size={18} color={ACCENT.amber.icon} />
                <span style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>Scoring Window & Probation</span>
              </div>
              <div style={fieldRow}>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Rolling Window (days)</label>
                  <input type="number" min="1" max="365" step="1" value={config.windowDays ?? 30}
                    onChange={(e) => update('windowDays', Math.max(1, parseInt(e.target.value) || 30))} style={numInput} />
                  <div style={descStyle}>Default: 30</div>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Probation Duration (days)</label>
                  <input type="number" min="1" max="90" step="1" value={config.probationDays ?? 7}
                    onChange={(e) => update('probationDays', Math.max(1, parseInt(e.target.value) || 7))} style={numInput} />
                  <div style={descStyle}>Default: 7</div>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Auto-Regress on BLOCK</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4 }}>
                    <input type="checkbox" checked={config.autoRegressionOnBlock ?? true}
                      onChange={(e) => update('autoRegressionOnBlock', e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: config.autoRegressionOnBlock ? '#dc2626' : S[600] }}>
                      {config.autoRegressionOnBlock ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                  <div style={descStyle}>Immediately demote on any BLOCK</div>
                </div>
              </div>
            </div>

            {/* Org Goals (only show on ORG or TENANT scope, and only if user has admin rights) */}
            {(activeScope === 'ORG' || activeScope === 'TENANT') && canEditGoals ? (
              <div style={sectionCard(ACCENT.emerald.icon)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <BarChart3 size={18} color={ACCENT.emerald.icon} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>Organizational Trust Goals</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: ACCENT.rose.bg, color: ACCENT.rose.text }}>Admin Only</span>
                </div>
                <div style={fieldRow}>
                  <div style={fieldGroup}>
                    <label style={labelStyle}>Target Trust Score</label>
                    <input type="number" min="50" max="100" step="0.5" value={config.targetTrustScore ?? 95}
                      onChange={(e) => update('targetTrustScore', parseFloat(e.target.value) || 95)}
                      style={{ ...numInput, borderLeft: '4px solid #16a34a' }} />
                  </div>
                  <div style={fieldGroup}>
                    <label style={labelStyle}>Target Autonomy Level</label>
                    <select value={config.targetAutonomyLevel || 'SUPERVISED'}
                      onChange={(e) => update('targetAutonomyLevel', e.target.value)} style={{ ...selectStyle, width: '100%' }}>
                      <option value="SELF_AUTONOMOUS">Self-Autonomous</option>
                      <option value="SUPERVISED">Supervised</option>
                      <option value="RESTRICTED">Restricted</option>
                    </select>
                  </div>
                  <div style={fieldGroup}>
                    <label style={labelStyle}>Risk Tolerance</label>
                    <select value={config.orgRiskTolerance || 'MODERATE'}
                      onChange={(e) => update('orgRiskTolerance', e.target.value)} style={{ ...selectStyle, width: '100%' }}>
                      <option value="CONSERVATIVE">Conservative</option>
                      <option value="MODERATE">Moderate</option>
                      <option value="AGGRESSIVE">Aggressive</option>
                    </select>
                  </div>
                </div>
                <div style={fieldRow}>
                  <div style={fieldGroup}>
                    <label style={labelStyle}>Compliance Deadline</label>
                    <input type="date" value={config.complianceDeadline || ''}
                      onChange={(e) => update('complianceDeadline', e.target.value || null)}
                      style={{ ...inputBase, width: 200, fontFamily: 'monospace' }} />
                  </div>
                  <div style={{ ...fieldGroup, flex: '2 1 400px' }}>
                    <label style={labelStyle}>Board Report Note</label>
                    <input type="text" value={config.boardReportNote || ''}
                      onChange={(e) => update('boardReportNote', e.target.value || null)}
                      placeholder="Custom note displayed on board reports..." style={inputBase} />
                  </div>
                </div>
              </div>
            ) : (activeScope === 'ORG' || activeScope === 'TENANT') && !canEditGoals ? (
              <div style={{ ...sectionCard(S[300]), opacity: 0.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BarChart3 size={18} color={S[400]} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: S[400] }}>Organizational Trust Goals</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: S[100], color: S[500] }}>Requires Admin</span>
                </div>
                <p style={{ fontSize: 13, color: S[400], marginTop: 8 }}>Only Super Admin and Org Admin can configure trust targets.</p>
              </div>
            ) : null}

            {/* Existing Overrides Summary */}
            {allConfigs.length > 0 && (
              <div style={{ background: S[50], borderRadius: 10, padding: 20, border: `1px solid ${S[200]}`, marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: S[900], marginBottom: 12 }}>Active Config Overrides</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {allConfigs.map((c, i) => {
                    const meta = SCOPE_META[c.configScope] || SCOPE_META.TENANT;
                    const agentName = c.agentId ? (agents.find(a => a.id === c.agentId)?.name || c.agentId.substring(0, 8)) : null;
                    return (
                      <button key={i}
                        onClick={() => {
                          setActiveScope(c.configScope);
                          if (c.configScope === 'AGENT') setSelectedAgentId(c.agentId);
                        }}
                        style={{
                          ...btnBase, padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                          background: '#fff', border: `1px solid ${meta.border}`,
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                        <meta.icon size={13} color={meta.color} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: meta.color }}>{meta.label}</span>
                        {agentName && <span style={{ fontSize: 11, color: S[500] }}>({agentName})</span>}
                        <span style={{ fontSize: 10, color: S[400] }}>
                          mult={c.evaluationMultiplier} blk={Number(c.blockWeight).toFixed(1)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Formula Reference */}
            <div style={{ background: S[50], borderRadius: 10, padding: 20, border: `1px solid ${S[200]}`, borderLeft: `4px solid ${BRAND.primary}` }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: S[900], marginBottom: 12 }}>Formula Reference</div>
              <div style={{ fontFamily: 'monospace', fontSize: 13, color: S[600], lineHeight: 2 }}>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.primary, textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 4 }}>Step 1: Compliance Score</div>
                  <div><strong>effectiveEvals</strong> = rawEventCount × evaluationMultiplier</div>
                  <div><strong>weightedViolations</strong> = (blocks × blockWeight) + (warns × warnWeight) + (audits × auditWeight)</div>
                  <div><strong>complianceScore</strong> = (effectiveEvals − weightedViolations) / effectiveEvals × 100</div>
                </div>
                <div style={{ marginBottom: 8, paddingTop: 8, borderTop: `1px solid ${S[200]}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 4 }}>Step 2: Eval Ramp (Trust Maturity)</div>
                  <div><strong>rampFactor</strong> = min(1.0, effectiveEvals / minEvalsForScoring)</div>
                  <div><strong>initialScore</strong> = 50.00 <span style={{ color: S[400] }}>(external agents)</span> | 75.00 <span style={{ color: S[400] }}>(native agents)</span></div>
                  <div><strong>trustScore</strong> = initialScore + (complianceScore − initialScore) × rampFactor</div>
                </div>
                <div style={{ paddingTop: 8, borderTop: `1px solid ${S[200]}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 4 }}>Step 3: Scope Resolution</div>
                  <div><strong>AGENT</strong> → check for agent-specific override first</div>
                  <div><strong>TENANT</strong> → fall back to tenant config if no agent override</div>
                  <div><strong>ORG</strong> → fall back to org config if no tenant override</div>
                  <div><strong>System</strong> → use built-in defaults if no config exists at any scope</div>
                </div>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function AgentTrustCenterPage({ agentScope = null }) {
  const { user, isSuperAdmin, currentRole } = useAuth();
  const { currentOrg, currentTenant } = usePlatform();
  const { addToast } = useToast();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [report, setReport] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [loading, setLoading] = useState(false);
  const [allAgents, setAllAgents] = useState([]);

  const externalAgentIdSet = useMemo(() => {
    const set = new Set();
    allAgents.forEach(a => { if (a.isExternal) set.add(a.id); });
    return set;
  }, [allAgents]);

  const isAgentExternal = useCallback((agentId) => {
    if (!agentId) return false;
    return externalAgentIdSet.has(typeof agentId === 'object' ? agentId.toString?.() : String(agentId));
  }, [externalAgentIdSet]);

  const scopedProfiles = useMemo(() => {
    if (!agentScope) return profiles;
    return profiles.filter(p => {
      const ext = isAgentExternal(p.agentId?.toString?.() || p.agentId);
      return agentScope === 'native' ? !ext : agentScope === 'external' ? ext : true;
    });
  }, [profiles, agentScope, isAgentExternal]);

  const scopedAlerts = useMemo(() => {
    if (!agentScope) return alerts;
    return alerts.filter(a => {
      const ext = isAgentExternal(a.agentId?.toString?.() || a.agentId);
      return agentScope === 'native' ? !ext : agentScope === 'external' ? ext : true;
    });
  }, [alerts, agentScope, isAgentExternal]);

  const scopedDashboard = useMemo(() => {
    if (!dashboard) return null;
    if (!agentScope) return dashboard;
    const total = scopedProfiles.length;
    const avg = total > 0 ? scopedProfiles.reduce((s, p) => s + (p.currentScore || 0), 0) / total : 0;
    const probation = scopedProfiles.filter(p => (p.autonomyLevel || '').toUpperCase() === 'PROBATION').length;
    const anomalies = scopedAlerts.length;
    const riskMap = {}, autoMap = {};
    scopedProfiles.forEach(p => {
      const rl = (p.riskLevel || 'UNKNOWN').toUpperCase();
      riskMap[rl] = (riskMap[rl] || 0) + 1;
      const al = (p.autonomyLevel || 'RESTRICTED').toUpperCase();
      autoMap[al] = (autoMap[al] || 0) + 1;
    });
    return {
      ...dashboard, totalAgents: total, averageScore: Math.round(avg * 100) / 100,
      fleetAverageScore: Math.round(avg * 100) / 100, probationCount: probation,
      agentsOnProbation: probation, anomalyCount: anomalies, activeAnomalies: anomalies,
      agentsByRisk: riskMap, riskDistribution: riskMap, agentsByAutonomy: autoMap, autonomyDistribution: autoMap,
    };
  }, [dashboard, agentScope, scopedProfiles, scopedAlerts]);

  const [actionModal, setActionModal] = useState(null);
  const [actionData, setActionData] = useState({});

  const _isSuperAdmin = typeof isSuperAdmin === 'function' ? isSuperAdmin() : false;
  const isOrgAdmin = _isSuperAdmin || currentRole === 'Super Admin' || currentRole === 'Org Admin'
    || user?.role === 'Super Admin' || user?.role === 'Org Admin' || user?.role === 'org_admin' || user?.role === 'admin';
  const isTenantAdmin = currentRole === 'Tenant Admin' || user?.role === 'tenant_admin' || isOrgAdmin;
  const canAccessBoard = isOrgAdmin;
  const canModify = isOrgAdmin;

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    const res = await api.getTrustDashboard({ orgId: currentOrg?.id, tenantId: currentTenant?.id });
    setLoading(false);
    if (res.ok) setDashboard(res.data);
    else addToast('Failed to load dashboard: ' + res.error, 'error');
  }, [currentOrg, currentTenant, addToast]);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    const res = await api.getTrustProfiles({ orgId: currentOrg?.id, tenantId: currentTenant?.id });
    setLoading(false);
    if (res.ok) setProfiles(res.data || []);
    else addToast('Failed to load profiles: ' + res.error, 'error');
  }, [currentOrg, currentTenant, addToast]);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    const res = await api.getTrustProfilesToWatch({ orgId: currentOrg?.id, tenantId: currentTenant?.id });
    setLoading(false);
    if (res.ok) setAlerts(res.data || []);
    else addToast('Failed to load alerts: ' + res.error, 'error');
  }, [currentOrg, currentTenant, addToast]);

  const loadReport = useCallback(async () => {
    if (!canAccessBoard) return;
    setLoading(true);
    const res = await api.getTrustBoardReport({ orgId: currentOrg?.id, tenantId: currentTenant?.id, dateRange });
    setLoading(false);
    if (res.ok) setReport(res.data);
    else addToast('Failed to load report: ' + res.error, 'error');
  }, [currentOrg, currentTenant, dateRange, canAccessBoard, addToast]);

  const loadAllAgentsForScope = useCallback(async () => {
    try {
      const tenantId = currentTenant?.id || currentOrg?.tenantId;
      const res = await api.getAgents({ tenantId });
      const raw = res?.data ?? res;
      setAllAgents(Array.isArray(raw) ? raw : (raw?.content || []));
    } catch (_) {}
  }, [currentOrg, currentTenant]);

  useEffect(() => { if (agentScope) loadAllAgentsForScope(); }, [agentScope, loadAllAgentsForScope]);

  useEffect(() => {
    if (activeTab === 'dashboard') { loadDashboard(); if (agentScope) loadProfiles(); }
    else if (activeTab === 'fleet') loadProfiles();
    else if (activeTab === 'anomalies') loadAlerts();
    else if (activeTab === 'board') loadReport();
    else if (activeTab === 'timeline') loadProfiles();
    else if (activeTab === 'live-exec') loadProfiles();
  }, [activeTab, loadDashboard, loadProfiles, loadAlerts, loadReport]);

  const handleRecalculate = async (agentId) => {
    setLoading(true);
    const res = await api.recalculateTrustProfile(agentId);
    setLoading(false);
    if (res.ok) { addToast('Trust profile recalculated', 'success'); await loadProfiles(); await loadDashboard(); }
    else addToast('Failed: ' + res.error, 'error');
  };

  const handleProbation = (agentId) => { setActionModal('probation'); setActionData({ agentId }); };
  const handleProbationConfirm = async () => {
    setLoading(true);
    const res = await api.triggerProbation(actionData.agentId, actionData.reason || 'Manual trigger');
    setLoading(false);
    if (res.ok) { addToast('Agent placed on probation', 'success'); await loadProfiles(); await loadDashboard(); setActionModal(null); }
    else addToast('Failed: ' + res.error, 'error');
  };

  const handleRelease = async (agentId) => {
    setLoading(true);
    const res = await api.releaseProbation(agentId);
    setLoading(false);
    if (res.ok) { addToast('Agent released from probation', 'success'); await loadProfiles(); await loadDashboard(); }
    else addToast('Failed: ' + res.error, 'error');
  };

  const handleOverride = (agentId, currentLevel) => { setActionModal('override'); setActionData({ agentId, currentLevel }); };
  const handleOverrideConfirm = async () => {
    setLoading(true);
    const res = await api.manualAutonomyOverride(actionData.agentId, actionData.newLevel, actionData.reason || 'Manual override');
    setLoading(false);
    if (res.ok) { addToast('Autonomy level updated', 'success'); await loadProfiles(); await loadDashboard(); setActionModal(null); }
    else addToast('Failed: ' + res.error, 'error');
  };

  /* ─── Tab Config ─────────────────────────────────────────────────── */
  const TABS = [
    { key: 'dashboard', label: 'Dashboard', icon: BarChart3, desc: 'Trust posture overview, autonomy distribution, and risk summary', always: true },
    { key: 'fleet', label: 'Agent Fleet', icon: Activity, desc: 'Monitor and manage trust scores across all governed agents', show: isTenantAdmin },
    { key: 'anomalies', label: 'Anomalies & Alerts', icon: AlertTriangle, desc: 'Block spikes, score anomalies, and automated risk alerts', show: isTenantAdmin },
    { key: 'board', label: 'Board Report', icon: Shield, desc: 'CISO-ready trust and autonomy reports for executive stakeholders', show: canAccessBoard },
    { key: 'timeline', label: 'Trust Timeline', icon: GitBranch, desc: 'Historical trust score trends and autonomy level transitions', always: true },
    { key: 'live-exec', label: 'Live Execution Center', icon: Zap, desc: 'Real-time agent execution stream with policy enforcement results', always: true, live: true },
    { key: 'scoring-config', label: 'Scoring Config', icon: Activity, desc: 'Configure trust scoring weights, thresholds, and autonomy levels', show: canModify },
  ].filter(t => t.always || t.show);

  return (
    <div style={{
      height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column',
      background: S[50], overflow: 'hidden',
    }}>
      <TCStyles />

      {/* ── Sticky Header ──────────────────────────────────────────────── */}
      <div style={{
        flexShrink: 0, background: '#fff', borderBottom: `1px solid ${S[200]}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '20px 32px 0' }}>
          <PageHeader
            icon={ShieldCheck}
            title="Agent Trust Center"
            subtitle="CISO-ready monitoring and management of agent trust, autonomy, and risk"
            badge={agentScope ? (agentScope === 'external' ? 'ExteLets' : 'Lets') : undefined}
          />

          {/* Tab Navigation (Shadow Discovery style — full-width underline tabs) */}
          <div style={{
            display: 'flex', gap: 0, padding: '0',
          }}>
            {TABS.map(({ key, label, icon: Icon, live, desc }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  className={isActive ? '' : 'tc-tab'}
                  onClick={() => setActiveTab(key)}
                  title={desc}
                  style={{
                    flex: 1, padding: '14px 16px',
                    borderBottom: isActive ? `3px solid ${BRAND.primary}` : '3px solid transparent',
                    background: 'transparent', border: 'none', borderBottomStyle: 'solid',
                    cursor: 'pointer', fontSize: 12, fontWeight: isActive ? 700 : 500,
                    color: isActive ? BRAND.primary : S[500],
                    transition: 'all 0.15s ease',
                    display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Icon size={15} strokeWidth={isActive ? 2.2 : 1.8} />
                  {label}
                  {live && <span style={{
                    width: 6, height: 6, background: '#f43f5e', borderRadius: '50%',
                    animation: 'tcPulse 1.5s infinite', marginLeft: 2,
                  }} />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Active tab description bar ─── */}
      {(() => {
        const activeTabMeta = TABS.find(t => t.key === activeTab);
        if (!activeTabMeta) return null;
        const ActiveIcon = activeTabMeta.icon;
        return (
          <div style={{
            padding: '8px 24px', background: BRAND.gradientSubtle,
            borderBottom: `1px solid ${S[150]}`,
            fontSize: 11, color: S[500], fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
          }}>
            <ActiveIcon size={12} color={S[400]} />
            {activeTabMeta.desc}
          </div>
        );
      })()}

      {/* ── Scrollable Body ────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '24px 32px 40px' }}>
          {activeTab === 'dashboard' && <TrustDashboard dashboard={scopedDashboard} loading={loading} onRefresh={loadDashboard} />}
          {activeTab === 'fleet' && <AgentFleetTab profiles={scopedProfiles} loading={loading}
            onRecalculate={handleRecalculate} onProbation={handleProbation} onRelease={handleRelease} onOverride={handleOverride} canModify={canModify} />}
          {activeTab === 'anomalies' && <AnomaliesTab alerts={scopedAlerts} loading={loading}
            onProbation={handleProbation} onOverride={handleOverride} canModify={canModify} />}
          {activeTab === 'board' && <BoardReportTab report={report} loading={loading} dateRange={dateRange}
            onDateRangeChange={(r) => setDateRange(r)} agentScope={agentScope} isAgentExternal={isAgentExternal} />}
          {activeTab === 'timeline' && <TrustTimelineTab profiles={scopedProfiles} loading={loading} agentScope={agentScope} />}
          {activeTab === 'live-exec' && <LiveExecutionCenter profiles={scopedProfiles} loading={loading} agentScope={agentScope} />}
          {activeTab === 'scoring-config' && canModify && <ScoringConfigTab onSaved={loadDashboard} canEditGoals={isOrgAdmin}
            userRole={currentRole || user?.role || 'Viewer'} userOrgId={user?.orgId || currentOrg?.id} userTenantId={user?.tenantId || currentTenant?.id} />}
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      {actionModal === 'probation' && (
        <Modal title="Trigger Probation" onClose={() => setActionModal(null)}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Reason (optional)</label>
            <textarea value={actionData.reason || ''} onChange={(e) => setActionData({ ...actionData, reason: e.target.value })}
              style={{ ...inputBase, minHeight: 80, fontFamily: 'monospace' }}
              placeholder="Explain why this agent is being placed on probation..." />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GhostButton onClick={() => setActionModal(null)}>Cancel</GhostButton>
            <button onClick={handleProbationConfirm} style={{
              ...btnBase, padding: '9px 18px', fontSize: 12, fontWeight: 700,
              background: '#dc2626', color: '#fff', borderRadius: 8,
            }}>Confirm Probation</button>
          </div>
        </Modal>
      )}

      {actionModal === 'override' && (
        <Modal title="Manual Autonomy Override" onClose={() => setActionModal(null)}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Current Level: {actionData.currentLevel || 'Unknown'}</label>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>New Autonomy Level</label>
            <select value={actionData.newLevel || ''} onChange={(e) => setActionData({ ...actionData, newLevel: e.target.value })} style={selectStyle}>
              <option value="">Select Level</option>
              <option value="SELF_AUTONOMOUS">Self-Autonomous</option>
              <option value="SUPERVISED">Supervised</option>
              <option value="RESTRICTED">Restricted</option>
              <option value="PROBATION">Probation</option>
            </select>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Reason (optional)</label>
            <textarea value={actionData.reason || ''} onChange={(e) => setActionData({ ...actionData, reason: e.target.value })}
              style={{ ...inputBase, minHeight: 60, fontFamily: 'monospace' }}
              placeholder="Explain the override decision..." />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GhostButton onClick={() => setActionModal(null)}>Cancel</GhostButton>
            <PrimaryButton onClick={handleOverrideConfirm} disabled={!actionData.newLevel}>Apply Override</PrimaryButton>
          </div>
        </Modal>
      )}
    </div>
  );
}
